import React, { useState, useMemo, useEffect, useRef, useContext } from 'react';
import axios from '../../../../api/axios';
import { WorkspaceContext } from '../../../../contexts/WorkspaceContext';
import {
  ALL_CHANNELS,
  LEAD_RESPONSE_TYPES,
  normalizeResponseType,
  extractNoteText,
} from '../../constants/campaignConstants';
import {
  computeCohortReachability,
  getStepChannelCoverage,
  computeStepLeadBreakdown,
  getLeadChannelContactValue,
} from '../../utils/campaignReachabilityUtils';
import {
  DEFAULT_TIMING_CONFIG,
  TIMING_PRESETS,
  simulateCadenceSchedule,
  parseDateTimeInTimezone,
  alignJourneyToTimingGuardrails,
  getDatePartsInTimezone,
  calculateNextEligibleSendTime,
  parseTimeToMinutes,
  getUserLocalTimezoneInfo,
  resolveCohortTimezoneSummary,
} from '../../utils/campaignTimingUtils';
import { formatLocalDate, addDays, calculateDiffDays } from '../../utils/dateUtils';
import { getStageClass } from '../../utils/campaignUtils';

const getChannelIcon = (ch) => {
  const raw = String(ch || '').toLowerCase();
  if (raw.includes('phone') || raw.includes('call')) return 'fas fa-phone-alt';
  if (raw.includes('whatsapp')) return 'fab fa-whatsapp';
  if (raw.includes('linkedin')) return 'fab fa-linkedin-in';
  if (raw.includes('telegram')) return 'fab fa-telegram-plane';
  return 'fas fa-envelope';
};

const resolveLeadChannelInfo = (lead, stepItem, routing) => {
  if (routing) {
    if (routing.routeType === 'fallback') {
      const channelName = routing.assignedChannel || 'Fallback';
      return {
        channel: channelName,
        icon: getChannelIcon(channelName),
        contactValue:
          routing.contactValue || (lead ? getLeadChannelContactValue(lead, channelName) : ''),
        routeType: 'fallback',
        label: `${channelName} (Fallback)`,
        title: routing.reason || `Routed to fallback channel: ${channelName}`,
      };
    }
    if (routing.routeType === 'unreachable') {
      const channelName = stepItem?.channel || 'Email';
      return {
        channel: channelName,
        icon: 'fas fa-ban',
        contactValue: '',
        routeType: 'unreachable',
        label: `Missing ${channelName} (Unreachable)`,
        title: routing.reason || `No contact details found for ${channelName}`,
      };
    }
    if (routing.routeType === 'primary') {
      const channelName = routing.assignedChannel || stepItem?.channel || 'Email';
      return {
        channel: channelName,
        icon: getChannelIcon(channelName),
        contactValue:
          routing.contactValue || (lead ? getLeadChannelContactValue(lead, channelName) : ''),
        routeType: 'primary',
        label: channelName,
        title: `Primary Channel: ${channelName}`,
      };
    }
  }

  // Standalone fallback if routing was not computed for this lead
  if (lead && stepItem) {
    const primaryVal = getLeadChannelContactValue(lead, stepItem.channel);
    if (primaryVal) {
      return {
        channel: stepItem.channel,
        icon: getChannelIcon(stepItem.channel),
        contactValue: primaryVal,
        routeType: 'primary',
        label: stepItem.channel,
        title: `Primary Channel: ${stepItem.channel}`,
      };
    }
    if (lead.phone || lead.mobile || lead.telephone) {
      const isWa = String(stepItem.channel).toLowerCase().includes('whatsapp');
      const chName = isWa ? 'WhatsApp' : 'Phone Call';
      return {
        channel: chName,
        icon: getChannelIcon(chName),
        contactValue: lead.phone || lead.mobile || lead.telephone,
        routeType: 'fallback',
        label: `${chName} (Fallback)`,
        title: `Missing ${stepItem.channel} → using Phone`,
      };
    }
    if (lead.linkedinUrl || lead.linkedin_url || lead.social?.linkedin || lead.linkedin) {
      return {
        channel: 'LinkedIn',
        icon: getChannelIcon('LinkedIn'),
        contactValue:
          lead.linkedinUrl || lead.linkedin_url || lead.social?.linkedin || lead.linkedin,
        routeType: 'fallback',
        label: 'LinkedIn (Fallback)',
        title: `Missing ${stepItem.channel} → using LinkedIn`,
      };
    }
    if (lead.email) {
      return {
        channel: 'Email',
        icon: getChannelIcon('Email'),
        contactValue: lead.email,
        routeType: 'fallback',
        label: 'Email (Fallback)',
        title: `Missing ${stepItem.channel} → using Email`,
      };
    }
  }

  return {
    channel: stepItem?.channel || 'Email',
    icon: 'fas fa-ban',
    contactValue: '',
    routeType: 'unreachable',
    label: `Missing ${stepItem?.channel || 'Contact'} (Unreachable)`,
    title: 'No contact details available',
  };
};

const SIMPLE_CHANNELS = [
  { id: 'Email', label: 'Email Outreach' },
  { id: 'LinkedIn', label: 'LinkedIn / InMail' },
  { id: 'WhatsApp', label: 'WhatsApp Direct' },
  { id: 'Telegram', label: 'Telegram Direct' },
  { id: 'Phone Call', label: 'Sales Phone Call' },
  { id: 'SMS', label: 'SMS Direct' },
];

const SIMPLE_FALLBACKS = [
  { id: 'Auto', label: 'Auto (Smart Dynamic Routing)' },
  { id: 'Email', label: 'Email Outreach' },
  { id: 'LinkedIn', label: 'LinkedIn / InMail' },
  { id: 'WhatsApp', label: 'WhatsApp Direct' },
  { id: 'Telegram', label: 'Telegram Direct' },
  { id: 'Phone Call', label: 'Sales Phone Call' },
  { id: 'SMS', label: 'SMS Direct' },
  { id: 'None', label: 'None (Do Not Fallback)' },
];

const SIMPLE_CONDITIONS = [
  'Initial Touch',
  'If No Reply',
  'If Opened / Clicked',
  'If Positive Reply',
  'If No Response after 48h',
];

export default function Step4TeamAssign({
  formData,
  setFormData,
  isReadOnly = false,
  isCreateMode = false,
  hasGeneratedSequence = false,
  onGeneratedSequence,
  onAddJourneyStep,
  onUpdateJourneyStep,
  onRemoveJourneyStep,
  onLaunchOutreach,
  onPersistTouchpointStatus,
  isGeneratingSequence: isGeneratingSequenceProp,
  setIsGeneratingSequence: setIsGeneratingSequenceProp,
  initialOpenStatusStepIndex = null,
  initialGuardrailModal = null,
  initialLeadInterestedModal = null,
  initialSendingWindowWarningModal = null,
  isPersonalAccount: isPersonalAccountProp,
  isPersonal: isPersonalProp,
}) {
  const effectiveLeads = useMemo(() => {
    if (Array.isArray(formData?.importedLeads) && formData.importedLeads.length > 0) {
      return formData.importedLeads;
    }
    if (Array.isArray(formData?.targetLeads) && formData.targetLeads.length > 0) {
      return formData.targetLeads;
    }
    if (
      Array.isArray(formData?.strategy?.importedLeads) &&
      formData.strategy.importedLeads.length > 0
    ) {
      return formData.strategy.importedLeads;
    }
    if (
      Array.isArray(formData?.strategy?.targetLeads) &&
      formData.strategy.targetLeads.length > 0
    ) {
      return formData.strategy.targetLeads;
    }
    if (Array.isArray(formData?.leads) && formData.leads.length > 0) {
      return formData.leads;
    }
    return [];
  }, [
    formData?.importedLeads,
    formData?.targetLeads,
    formData?.strategy?.importedLeads,
    formData?.strategy?.targetLeads,
    formData?.leads,
  ]);

  const reachabilityStats = useMemo(() => {
    return computeCohortReachability(effectiveLeads);
  }, [effectiveLeads]);

  const [internalGenerating, setInternalGenerating] = useState(false);
  const isGeneratingAi =
    isGeneratingSequenceProp !== undefined ? isGeneratingSequenceProp : internalGenerating;
  const setIsGeneratingAi = (val) => {
    setInternalGenerating(val);
    if (setIsGeneratingSequenceProp) setIsGeneratingSequenceProp(val);
  };
  const [aiError, setAiError] = useState('');

  // ── Step Lead Routing Breakdown State ──
  const [openBreakdownStepIndex, setOpenBreakdownStepIndex] = useState(null);
  const [activeBreakdownTab, setActiveBreakdownTab] = useState('all');
  const [stepLeadOverrides, setStepLeadOverrides] = useState({});

  // ── Step Delivery & Response Records Drawer State ──
  const [openStatusStepIndex, setOpenStatusStepIndex] = useState(initialOpenStatusStepIndex);
  const [activeDeliveryTab, setActiveDeliveryTab] = useState('all');
  const [activeResponseTab, setActiveResponseTab] = useState('all');
  const [leadDeliveryMap, setLeadDeliveryMap] = useState(() => {
    const initial = {};
    if (Array.isArray(formData?.journey)) {
      formData.journey.forEach((step, idx) => {
        if (step?.leadOutreachStatus && Object.keys(step.leadOutreachStatus).length > 0) {
          initial[idx] = { ...step.leadOutreachStatus };
        }
      });
    }
    return initial;
  });
  const [leadResponsesMap, setLeadResponsesMap] = useState(() => {
    const initial = {};
    if (Array.isArray(formData?.journey)) {
      formData.journey.forEach((step, idx) => {
        if (step?.leadResponses && Object.keys(step.leadResponses).length > 0) {
          initial[idx] = { ...step.leadResponses };
        }
      });
    }
    return initial;
  });

  const [hoveredBubbleIndex, setHoveredBubbleIndex] = useState(null);
  const [selectedBubbleIndex, setSelectedBubbleIndex] = useState(
    initialOpenStatusStepIndex !== null && initialOpenStatusStepIndex !== undefined
      ? initialOpenStatusStepIndex
      : 0
  );
  const [isEditingStep, setIsEditingStep] = useState(false);
  const [openEditDropdown, setOpenEditDropdown] = useState(null); // 'channel' | 'fallback' | 'condition' | null
  const editDropdownContainerRef = useRef(null);

  useEffect(() => {
    const handleDocumentClick = (e) => {
      if (
        editDropdownContainerRef.current &&
        !editDropdownContainerRef.current.contains(e.target)
      ) {
        setOpenEditDropdown(null);
      }
    };
    if (openEditDropdown) {
      document.addEventListener('mousedown', handleDocumentClick);
    }
    return () => {
      document.removeEventListener('mousedown', handleDocumentClick);
    };
  }, [openEditDropdown]);

  const [activeBelowGridTab, setActiveBelowGridTab] = useState(
    initialOpenStatusStepIndex !== null && initialOpenStatusStepIndex !== undefined
      ? 'delivery'
      : 'reachability'
  );

  const handleJumpToStep = (idx) => {
    setSelectedBubbleIndex(idx);
    const target = document.getElementById(`cmp-step-card-${idx}`);
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      target.classList.add('cmp-step-card-highlight');
      setTimeout(() => {
        target.classList.remove('cmp-step-card-highlight');
      }, 1600);
    }
  };

  const handleAddTouchpointAndFocus = () => {
    if (isGeneratingAi || isLastStepAtOrPastEnd) return;
    const newIdx = formData?.journey ? formData.journey.length : 0;
    onAddJourneyStep();
    setSelectedBubbleIndex(newIdx);
    setIsEditingStep(true);
    setHoveredBubbleIndex(null);
    setTimeout(() => {
      const el = document.getElementById(`cmp-step-card-${newIdx}`);
      if (el && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else {
        const col = document.querySelector('.cmp-bubble-flow-column');
        if (col && typeof col.scrollTo === 'function') {
          col.scrollTo({ top: col.scrollHeight, behavior: 'smooth' });
        }
      }
    }, 60);
  };

  useEffect(() => {
    if (selectedBubbleIndex !== null && selectedBubbleIndex !== undefined) {
      const timer = setTimeout(() => {
        const target = document.getElementById(`cmp-step-card-${selectedBubbleIndex}`);
        if (target && typeof target.scrollIntoView === 'function') {
          target.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [selectedBubbleIndex]);

  // Sync internal delivery and responses map whenever formData.journey updates from external props/saves
  useEffect(() => {
    if (!Array.isArray(formData?.journey)) return;
    setLeadDeliveryMap((prev) => {
      let changed = false;
      const next = { ...prev };
      formData.journey.forEach((step, idx) => {
        if (step?.leadOutreachStatus !== undefined) {
          next[idx] = { ...step.leadOutreachStatus };
          changed = true;
        }
      });
      return changed ? next : prev;
    });
    setLeadResponsesMap((prev) => {
      let changed = false;
      const next = { ...prev };
      formData.journey.forEach((step, idx) => {
        if (step?.leadResponses !== undefined) {
          next[idx] = { ...step.leadResponses };
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [formData?.journey]);

  // ── Workspace Context & Campaign Team Members State ──
  const workspaceCtx = useContext(WorkspaceContext);
  const workspaceId = workspaceCtx?.workspaceId;

  const isPersonalAccount =
    isPersonalAccountProp !== undefined
      ? Boolean(isPersonalAccountProp)
      : isPersonalProp !== undefined
        ? Boolean(isPersonalProp)
        : Boolean(
            workspaceCtx?.isPersonalWorkspace ||
            workspaceCtx?.isPersonalUser ||
            workspaceCtx?.workspaceType === 'personal' ||
            workspaceCtx?.primaryWorkspace?.workspaceType === 'personal'
          );

  const [workspaceMembers, setWorkspaceMembers] = useState([]);
  const [selectedNewMemberId, setSelectedNewMemberId] = useState('');

  // Normalized list of team members assigned to this campaign
  const campaignMembers = useMemo(() => {
    const raw = formData?.assignedMembers || formData?.strategy?.assignedMembers || [];
    if (!Array.isArray(raw)) return [];
    return raw.map((m) => {
      if (typeof m === 'object' && m !== null) {
        return {
          id: m.id || m.userId || m.user_id || m.email,
          userId: m.userId || m.user_id || m.id,
          name: m.name || m.display_name || m.username || m.email || 'Team Member',
          email: m.email || '',
          role: m.role || 'Campaign Rep',
        };
      }
      return {
        id: String(m),
        userId: String(m),
        name: String(m),
        email: '',
        role: 'Campaign Rep',
      };
    });
  }, [formData?.assignedMembers, formData?.strategy?.assignedMembers]);

  // Fetch workspace members if in a workspace context (organization workspace)
  useEffect(() => {
    if (!workspaceId || isPersonalAccount) return;
    let mounted = true;
    axios
      .get(`/workspace/${workspaceId}/members`)
      .then((res) => {
        if (mounted && res.data?.success && Array.isArray(res.data.members)) {
          setWorkspaceMembers(res.data.members);
        }
      })
      .catch((err) => {
        console.warn('[Step4TeamAssign] Could not load workspace members:', err);
      });
    return () => {
      mounted = false;
    };
  }, [workspaceId, isPersonalAccount]);

  const availableWorkspaceMembers = useMemo(() => {
    return workspaceMembers.filter((wm) => {
      const wId = String(wm.user_id || wm.id);
      return !campaignMembers.some((cm) => String(cm.userId || cm.id) === wId);
    });
  }, [workspaceMembers, campaignMembers]);

  const handleAddCampaignMember = () => {
    if (!selectedNewMemberId) return;
    const memberObj = workspaceMembers.find(
      (wm) => String(wm.user_id || wm.id) === String(selectedNewMemberId)
    );
    if (!memberObj) return;

    const newEntry = {
      id: memberObj.id || memberObj.user_id,
      userId: memberObj.user_id || memberObj.id,
      name:
        memberObj.display_name ||
        memberObj.name ||
        memberObj.username ||
        memberObj.email ||
        'Team Member',
      email: memberObj.email || '',
      role: memberObj.role || 'Campaign Rep',
    };
    const nextMembers = [...campaignMembers, newEntry];
    setFormData((prev) => ({
      ...prev,
      assignedMembers: nextMembers,
      strategy: { ...(prev?.strategy || {}), assignedMembers: nextMembers },
    }));
    if (typeof onPersistTouchpointStatus === 'function') {
      onPersistTouchpointStatus(0, { assignedMembers: nextMembers });
    }
    setSelectedNewMemberId('');
  };

  const handleRemoveCampaignMember = (memId) => {
    const nextMembers = campaignMembers.filter((m) => String(m.userId || m.id) !== String(memId));
    setFormData((prev) => ({
      ...prev,
      assignedMembers: nextMembers,
      strategy: { ...(prev?.strategy || {}), assignedMembers: nextMembers },
    }));
    if (typeof onPersistTouchpointStatus === 'function') {
      onPersistTouchpointStatus(0, { assignedMembers: nextMembers });
    }
  };

  // ── "Lead interested" Modal State & Task Management ──
  const [leadInterestedModal, setLeadInterestedModal] = useState(
    initialLeadInterestedModal || {
      isOpen: false,
      stepIdx: null,
      lead: null,
      leadKey: '',
      assignedMember: '',
      note: '',
      tasks: [],
      taskInput: '',
      taskDueDate: '',
      taskPriority: 'Medium',
      taskAssignee: '',
    }
  );

  const handleCloseLeadInterestedModal = () => {
    setLeadInterestedModal((prev) => ({
      ...prev,
      isOpen: false,
      stepIdx: null,
      lead: null,
      leadKey: '',
      taskInput: '',
    }));
  };

  const handleAddTaskToInterestedModal = () => {
    const raw = leadInterestedModal.taskInput || '';
    if (!raw.trim()) return;
    const lines = raw.split('\n');
    const title = lines[0].trim();
    const description = lines.slice(1).join('\n').trim();
    if (!title) return;

    const newTask = {
      id: 'task_' + Date.now() + '_' + Math.random().toString(36).substr(2, 5),
      title,
      description,
      dueDate: leadInterestedModal.taskDueDate || '',
      priority: leadInterestedModal.taskPriority || 'Medium',
      assignee: leadInterestedModal.taskAssignee || leadInterestedModal.assignedMember || '',
      status: 'pending',
      createdAt: new Date().toISOString(),
    };

    setLeadInterestedModal((prev) => ({
      ...prev,
      tasks: [...prev.tasks, newTask],
      taskInput: '',
      taskDueDate: '',
    }));
  };

  const handleToggleTaskStatusInInterestedModal = (taskId) => {
    setLeadInterestedModal((prev) => ({
      ...prev,
      tasks: prev.tasks.map((t) => {
        if (t.id === taskId) {
          const nextStatus = t.status === 'completed' ? 'in_progress' : 'completed';
          return { ...t, status: nextStatus };
        }
        return t;
      }),
    }));
  };

  const handleUpdateTaskStatusInInterestedModal = (taskId, newStatus) => {
    setLeadInterestedModal((prev) => ({
      ...prev,
      tasks: prev.tasks.map((t) => (t.id === taskId ? { ...t, status: newStatus } : t)),
    }));
  };

  const handleDeleteTaskInInterestedModal = (taskId) => {
    setLeadInterestedModal((prev) => ({
      ...prev,
      tasks: prev.tasks.filter((t) => t.id !== taskId),
    }));
  };

  // ── Guardrail Violation Double-Confirm Modal State ──
  const [guardrailModal, setGuardrailModal] = useState(
    initialGuardrailModal || {
      isOpen: false,
      stepIdx: null,
      proposedDate: '',
      proposedTime: '',
      proposedDay: 1,
      violations: [],
      recommendedDate: '',
      recommendedTime: '',
      recommendedDay: 1,
    }
  );

  // ── Custom Status Dropdown Menu State ──
  const [statusDropdownOpenStepIndex, setStatusDropdownOpenStepIndex] = useState(null);
  const statusDropdownRef = useRef(null);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(e.target)) {
        setStatusDropdownOpenStepIndex(null);
      }
    };
    if (statusDropdownOpenStepIndex !== null) {
      document.addEventListener('mousedown', handleOutsideClick);
    }
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [statusDropdownOpenStepIndex]);

  // ── Allowed Sending Window Warning Modal State ──
  const sendingWindowColRef = useRef(null);
  const [highlightSendingWindow, setHighlightSendingWindow] = useState(false);
  const [sendingWindowWarningModal, setSendingWindowWarningModal] = useState(
    initialSendingWindowWarningModal || {
      isOpen: false,
      stepIdx: null,
      proposedTime: '',
      currentDate: '',
      currentWindowStart: '09:00',
      currentWindowEnd: '18:00',
      newProposedStart: '09:00',
      newProposedEnd: '18:00',
      isBeforeStart: false,
      isAfterEnd: false,
      message: '',
    }
  );

  const scrollToSendingWindow = () => {
    setTimeout(() => {
      if (sendingWindowColRef.current) {
        sendingWindowColRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else {
        const col = document.querySelector('.cmp-timing-col');
        if (col) {
          col.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
      setHighlightSendingWindow(true);
      setTimeout(() => setHighlightSendingWindow(false), 3000);
    }, 50);
  };

  const [clickedAttentionSteps, setClickedAttentionSteps] = useState(() => new Set());
  const [currentScheduleTimestamp, setCurrentScheduleTimestamp] = useState(() => Date.now());
  const campaignStartDate = formData?.startDate || formatLocalDate(new Date());
  const campaignDurationDays = useMemo(() => {
    const raw = Number(formData?.durationDays);
    if (!isNaN(raw) && raw > 0) return raw;
    if (formData?.startDate && formData?.endDate) {
      return Math.max(1, calculateDiffDays(formData.startDate, formData.endDate));
    }
    return 45;
  }, [formData?.durationDays, formData?.startDate, formData?.endDate]);

  const campaignEndDate = useMemo(() => {
    if (formData?.endDate) return formData.endDate;
    if (campaignStartDate && campaignDurationDays) {
      return addDays(campaignStartDate, campaignDurationDays);
    }
    return '';
  }, [formData?.endDate, campaignStartDate, campaignDurationDays]);

  // Total inclusive calendar days from campaignStartDate to campaignEndDate (inclusive)
  const maxCampaignDay = useMemo(() => {
    if (campaignEndDate && campaignStartDate) {
      return Math.max(1, calculateDiffDays(campaignStartDate, campaignEndDate) + 1);
    }
    return campaignDurationDays;
  }, [campaignStartDate, campaignEndDate, campaignDurationDays]);

  // ── Frequency & Timing Guardrails State ──
  const userLocalTz = useMemo(() => getUserLocalTimezoneInfo(), []);

  const timingConfig = useMemo(() => {
    return {
      ...DEFAULT_TIMING_CONFIG,
      fixedTimezone: userLocalTz?.timezone || 'Asia/Kuala_Lumpur',
      ...(formData?.schedule || {}),
      ...(formData?.strategy?.schedule || {}),
      ...(formData?.timingConfig || {}),
    };
  }, [
    formData?.schedule,
    formData?.strategy?.schedule,
    formData?.timingConfig,
    userLocalTz?.timezone,
  ]);

  const computeDefaultNominalDate = (dayNum) => {
    try {
      const clampedDay = Math.max(1, Math.min(maxCampaignDay, parseInt(dayNum, 10) || 1));
      const offset = Math.max(0, clampedDay - 1);
      const rawDate = addDays(campaignStartDate, offset) || campaignStartDate;
      const targetTz = timingConfig.fixedTimezone || 'Asia/Kuala_Lumpur';
      const sendTime = timingConfig.sendWindowStart || '09:00';
      const candidateDt = parseDateTimeInTimezone(rawDate, sendTime, targetTz);
      const eligible = calculateNextEligibleSendTime({
        targetTime: candidateDt,
        prospectTimezone: targetTz,
        config: timingConfig,
      });
      if (eligible?.nextEligibleTime) {
        const parts = getDatePartsInTimezone(eligible.nextEligibleTime, targetTz);
        const resolvedDate = parts?.dateKey || formatLocalDate(eligible.nextEligibleTime);
        return campaignEndDate && resolvedDate > campaignEndDate ? campaignEndDate : resolvedDate;
      }
      return campaignEndDate && rawDate > campaignEndDate ? campaignEndDate : rawDate;
    } catch {
      return formatLocalDate(new Date());
    }
  };

  const computeDayFromDate = (dateStr) => {
    try {
      if (!dateStr) return 1;
      const diff = calculateDiffDays(campaignStartDate, dateStr);
      const day = Math.max(1, diff + 1);
      return Math.min(maxCampaignDay, day);
    } catch {
      return 1;
    }
  };

  const hasAnyStepExceedingDuration = useMemo(() => {
    if (!Array.isArray(formData?.journey) || formData.journey.length === 0) return false;
    return formData.journey.some((step) => {
      const sDate = step.nominalDate || computeDefaultNominalDate(step.day);
      const sDay = Number(step.day) || 1;
      return (
        (campaignEndDate && sDate > campaignEndDate) ||
        (campaignStartDate && sDate < campaignStartDate) ||
        sDay > maxCampaignDay ||
        sDay < 1
      );
    });
  }, [
    formData?.journey,
    campaignStartDate,
    campaignEndDate,
    maxCampaignDay,
    computeDefaultNominalDate,
  ]);

  const isLastStepAtOrPastEnd = useMemo(() => {
    if (!Array.isArray(formData?.journey) || formData.journey.length === 0) return false;
    const lastStep = formData.journey[formData.journey.length - 1];
    const sDate = lastStep.nominalDate || computeDefaultNominalDate(lastStep.day);
    const sDay = Number(lastStep.day) || 1;
    return sDay >= maxCampaignDay || Boolean(campaignEndDate && sDate >= campaignEndDate);
  }, [formData?.journey, maxCampaignDay, campaignEndDate, computeDefaultNominalDate]);

  const handleOverrideLeadChannel = (stepIdx, leadId, newChannel) => {
    setStepLeadOverrides((prev) => ({
      ...prev,
      [stepIdx]: {
        ...(prev[stepIdx] || {}),
        [leadId]: newChannel,
      },
    }));
  };

  const updateTimingConfig = (key, value) => {
    if (isReadOnly) return;
    const nextConfig = {
      ...timingConfig,
      [key]: value,
    };

    if (key === 'excludeWeekends' && value === true) {
      const currentDays = Array.isArray(timingConfig.businessDays)
        ? timingConfig.businessDays
        : [1, 2, 3, 4, 5];
      const filtered = currentDays.filter((d) => d !== 6 && d !== 7);
      nextConfig.businessDays = filtered.length > 0 ? filtered : [1, 2, 3, 4, 5];
    }

    if (key === 'sendWindowStart' || key === 'sendWindowEnd') {
      const qStart = parseTimeToMinutes(nextConfig.quietHoursStart || '18:00');
      const qEnd = parseTimeToMinutes(nextConfig.quietHoursEnd || '09:00');
      const sStart = parseTimeToMinutes(nextConfig.sendWindowStart || '09:00');
      const sEnd = parseTimeToMinutes(nextConfig.sendWindowEnd || '18:00');

      const isViolatingQuietHours =
        qStart > qEnd ? sStart < qEnd || sEnd > qStart : sStart < qEnd && sEnd > qStart;

      if (isViolatingQuietHours) {
        nextConfig.quietHoursEnabled = false;
      }
    }

    if (key === 'quietHoursEnabled' && value === true) {
      const qStart = parseTimeToMinutes(nextConfig.quietHoursStart || '18:00');
      const qEnd = parseTimeToMinutes(nextConfig.quietHoursEnd || '09:00');
      const sStart = parseTimeToMinutes(nextConfig.sendWindowStart || '09:00');
      const sEnd = parseTimeToMinutes(nextConfig.sendWindowEnd || '18:00');

      if (qStart > qEnd) {
        if (sStart < qEnd) {
          nextConfig.sendWindowStart = nextConfig.quietHoursEnd || '09:00';
        }
        if (sEnd > qStart) {
          nextConfig.sendWindowEnd = nextConfig.quietHoursStart || '18:00';
        }
      }
    }

    const targetTz = nextConfig.fixedTimezone || 'Asia/Kuala_Lumpur';
    let nextJourney = formData?.journey;

    if (
      key === 'excludeWeekends' ||
      key === 'businessDays' ||
      key === 'fixedTimezone' ||
      key === 'quietHoursEnabled'
    ) {
      if (Array.isArray(formData?.journey) && formData.journey.length > 0) {
        nextJourney = alignJourneyToTimingGuardrails({
          journey: formData.journey,
          startDate: campaignStartDate,
          endDate: campaignEndDate,
          config: nextConfig,
          timezone: targetTz,
        });
      }
    } else if (key === 'sendWindowStart' || key === 'sendWindowEnd') {
      const startMin = parseTimeToMinutes(nextConfig.sendWindowStart || '09:00');
      const endMin = parseTimeToMinutes(nextConfig.sendWindowEnd || '18:00');
      if (Array.isArray(formData?.journey) && formData.journey.length > 0) {
        nextJourney = formData.journey.map((step) => {
          const sTime = step.nominalTime || nextConfig.sendWindowStart || '09:00';
          const sMin = parseTimeToMinutes(sTime);
          if (sMin < startMin) {
            return { ...step, nominalTime: nextConfig.sendWindowStart || '09:00' };
          }
          if (sMin > endMin) {
            return { ...step, nominalTime: nextConfig.sendWindowEnd || '18:00' };
          }
          return step;
        });
      }
    }

    setFormData((prev) => ({
      ...prev,
      schedule: nextConfig,
      journey: nextJourney,
      strategy: {
        ...(prev?.strategy || {}),
        schedule: nextConfig,
        journey: nextJourney,
      },
    }));
  };

  const applyTimingPreset = (preset) => {
    if (isReadOnly || !preset?.config) return;
    const nextConfig = {
      ...timingConfig,
      ...preset.config,
    };
    const targetTz = nextConfig.fixedTimezone || 'Asia/Kuala_Lumpur';
    const nextJourney =
      Array.isArray(formData?.journey) && formData.journey.length > 0
        ? alignJourneyToTimingGuardrails({
            journey: formData.journey,
            startDate: campaignStartDate,
            endDate: campaignEndDate,
            config: nextConfig,
            timezone: targetTz,
          })
        : formData?.journey;

    setFormData((prev) => ({
      ...prev,
      schedule: nextConfig,
      journey: nextJourney,
      strategy: {
        ...(prev?.strategy || {}),
        schedule: nextConfig,
        journey: nextJourney,
      },
    }));
  };

  const toggleBusinessDay = (dayNum) => {
    if (isReadOnly) return;
    const currentDays = Array.isArray(timingConfig.businessDays)
      ? timingConfig.businessDays
      : [1, 2, 3, 4, 5];
    const exists = currentDays.includes(dayNum);
    const nextDays = exists
      ? currentDays.filter((d) => d !== dayNum)
      : [...currentDays, dayNum].sort((a, b) => a - b);
    if (nextDays.length === 0) return;

    const nextConfig = {
      ...timingConfig,
      businessDays: nextDays,
    };

    // When enabling weekends on day pills (Saturday = 6, Sunday = 7), the tick on excludeWeekends should be removed
    if (!exists && (dayNum === 6 || dayNum === 7)) {
      nextConfig.excludeWeekends = false;
    }

    const targetTz = nextConfig.fixedTimezone || 'Asia/Kuala_Lumpur';
    const nextJourney =
      Array.isArray(formData?.journey) && formData.journey.length > 0
        ? alignJourneyToTimingGuardrails({
            journey: formData.journey,
            startDate: campaignStartDate,
            endDate: campaignEndDate,
            config: nextConfig,
            timezone: targetTz,
          })
        : formData?.journey;

    setFormData((prev) => ({
      ...prev,
      schedule: nextConfig,
      journey: nextJourney,
      strategy: {
        ...(prev?.strategy || {}),
        schedule: nextConfig,
        journey: nextJourney,
      },
    }));
  };

  const simulatedCadence = useMemo(() => {
    return simulateCadenceSchedule({
      startDate: campaignStartDate,
      endDate: campaignEndDate,
      nominalHour: timingConfig.sendWindowStart || '09:00',
      journey: formData?.journey || [],
      config: timingConfig,
      prospectTimezone: timingConfig.fixedTimezone || 'Asia/Kuala_Lumpur',
    });
  }, [campaignStartDate, campaignEndDate, formData?.journey, timingConfig]);

  // ── Helper to resolve all alias keys for a lead ──
  const getLeadKeys = (lead) => {
    if (!lead) return [];
    return [lead.id, lead._id, lead.leadId, lead.pipelineLeadId, lead.email, lead.name]
      .filter((k) => k !== undefined && k !== null && String(k).trim() !== '')
      .map(String);
  };

  // ── Helper to update matching leads in all formData lead arrays ──
  const updateLeadsInFormData = (matchKeysOrPredicate, updateObjOrFn) => {
    setFormData((prev) => {
      const updateArray = (arr) => {
        if (!Array.isArray(arr)) return arr;
        return arr.map((l) => {
          const keys = getLeadKeys(l);
          const isMatch =
            typeof matchKeysOrPredicate === 'function'
              ? matchKeysOrPredicate(l, keys)
              : Array.isArray(matchKeysOrPredicate)
                ? matchKeysOrPredicate.some((k) => keys.includes(String(k)))
                : keys.includes(String(matchKeysOrPredicate));
          if (isMatch) {
            const patch = typeof updateObjOrFn === 'function' ? updateObjOrFn(l) : updateObjOrFn;
            return { ...l, ...patch };
          }
          return l;
        });
      };

      return {
        ...prev,
        importedLeads: updateArray(prev.importedLeads),
        targetLeads: updateArray(prev.targetLeads),
        strategy: prev.strategy
          ? {
              ...prev.strategy,
              importedLeads: updateArray(prev.strategy.importedLeads),
              targetLeads: updateArray(prev.strategy.targetLeads),
            }
          : prev.strategy,
      };
    });
  };

  // ── Helper to safely update lead pipeline stage via CRM API ──
  const safePatchLeadStage = (crmId, stage, lostReason) => {
    if (!crmId) return;
    try {
      const payload = { stage };
      if (lostReason !== undefined) payload.lostReason = lostReason;
      const res = axios.patch(`/lead-pipeline/${crmId}`, payload);
      if (res && typeof res.catch === 'function') {
        res.catch((err) => {
          console.warn('[Step4TeamAssign] Failed to update lead stage:', err);
        });
      }
      return res;
    } catch (err) {
      console.warn('[Step4TeamAssign] Failed to update lead stage:', err);
    }
  };

  // ── Helper to check if a lead has been sent using multi-key lookup ──
  const isLeadSent = (lead, deliveryMap) => {
    if (!lead || !deliveryMap) return false;
    const keys = getLeadKeys(lead);
    return keys.some((k) => deliveryMap[k]?.sent === true);
  };

  // ── Helper to resolve delivery record for a lead ──
  const getLeadDeliveryRecord = (lead, deliveryMap) => {
    if (!lead || !deliveryMap) return null;
    const keys = getLeadKeys(lead);
    for (const k of keys) {
      if (deliveryMap[k]) return deliveryMap[k];
    }
    return null;
  };

  // ── Helper to resolve response record for a lead ──
  const getLeadResponseRecord = (lead, responsesMap) => {
    if (!lead) return null;
    if (responsesMap) {
      const keys = getLeadKeys(lead);
      for (const k of keys) {
        if (responsesMap[k]) {
          const rec = responsesMap[k];
          return {
            ...rec,
            responseType: normalizeResponseType(rec.responseType),
            note: extractNoteText(rec.note),
          };
        }
      }
    }
    const rt = normalizeResponseType(lead.leadResponse);
    if (rt === LEAD_RESPONSE_TYPES.INTERESTED || rt === LEAD_RESPONSE_TYPES.NOT_INTERESTED) {
      return {
        responseType: rt,
        note:
          extractNoteText(lead.note) ||
          extractNoteText(lead.notes) ||
          (typeof lead.lostReason === 'string' ? lead.lostReason : '') ||
          '',
        respondedAt: lead.respondedAt || '',
      };
    }
    const stage = String(lead.stage || '').toLowerCase();
    if (stage === 'qualified' || stage === 'won') {
      return {
        responseType: LEAD_RESPONSE_TYPES.INTERESTED,
        note: extractNoteText(lead.note) || extractNoteText(lead.notes) || '',
        respondedAt: lead.respondedAt || '',
      };
    }
    if (
      stage === 'lost' &&
      (lead.lostReason === 'Not interested' || lead.lostReason === 'not_interested')
    ) {
      return {
        responseType: LEAD_RESPONSE_TYPES.NOT_INTERESTED,
        note:
          (typeof lead.lostReason === 'string' ? lead.lostReason : '') ||
          extractNoteText(lead.note) ||
          '',
        respondedAt: lead.respondedAt || '',
      };
    }
    return null;
  };

  // ── Helper to resolve routing breakdown for a lead ──
  const resolveLeadRoutingRecord = (lead, breakdown) => {
    if (!lead || !breakdown?.allLeads) return null;
    const leadId = String(lead.id || lead._id || '');
    const email = lead.email ? String(lead.email) : '';
    const name = lead.name ? String(lead.name) : '';
    return (
      breakdown.allLeads.find(
        (row) =>
          (leadId && String(row.leadId) === leadId) ||
          (email && row.lead?.email && String(row.lead.email) === email) ||
          (name && row.lead?.name && String(row.lead.name) === name)
      ) || null
    );
  };

  // ── Helper to check if a lead has replied in cadence (stops auto-outreach) ──
  const hasLeadReplied = (lead, journeyList, responsesMap) => {
    if (!lead) return false;
    const stage = String(lead.stage || '').toLowerCase();
    if (stage === 'new') return false;
    if (lead.optOut === true || lead.unsubscribed === true) return true;

    const keys = getLeadKeys(lead);
    const steps = Array.isArray(journeyList) ? journeyList : [];

    // 1. Check journey step responses first
    let hasExplicitPositiveOrNegative = false;
    let hasExplicitNoReplyInAnyStep = false;
    for (let i = 0; i < steps.length; i++) {
      const stepResponses = {
        ...(steps[i]?.leadResponses || {}),
        ...(responsesMap?.[i] || {}),
      };
      for (const k of keys) {
        const rec = stepResponses[k];
        if (rec) {
          const rt = normalizeResponseType(rec.responseType);
          if (rt === LEAD_RESPONSE_TYPES.INTERESTED || rt === LEAD_RESPONSE_TYPES.NOT_INTERESTED) {
            hasExplicitPositiveOrNegative = true;
          } else if (rt === LEAD_RESPONSE_TYPES.NO_REPLY) {
            hasExplicitNoReplyInAnyStep = true;
          }
        }
      }
    }

    if (hasExplicitPositiveOrNegative) return true;
    // If user explicitly marked this lead as no_reply in the journey, don't stop from auto-outreach
    if (hasExplicitNoReplyInAnyStep) return false;

    if (stage === 'lost' || stage === 'qualified') return true;
    const leadRt = normalizeResponseType(lead.leadResponse);
    if (
      leadRt === LEAD_RESPONSE_TYPES.INTERESTED ||
      leadRt === LEAD_RESPONSE_TYPES.NOT_INTERESTED
    ) {
      return true;
    }

    return false;
  };

  // ── Helper to check if a lead has replied/stopped before a specific step index ──
  const hasLeadRepliedBeforeStep = (lead, stepIdx, journeyList, responsesMap) => {
    if (!lead) return false;
    if (stepIdx === 0) {
      // Step 1: all imported leads are included unless explicitly opted out
      return lead.optOut === true || lead.unsubscribed === true;
    }

    const stage = String(lead.stage || '').toLowerCase();
    if (stage === 'new') return false;
    if (lead.optOut === true || lead.unsubscribed === true) return true;

    const keys = getLeadKeys(lead);
    const steps = Array.isArray(journeyList) ? journeyList : [];
    const maxStep = Math.min(stepIdx, steps.length);

    // 1. Check if the lead responded in any prior journey step
    let hasExplicitPriorPositiveOrNegative = false;
    let hasExplicitPriorNoReply = false;
    for (let i = 0; i < maxStep; i++) {
      const stepResponses = {
        ...(steps[i]?.leadResponses || {}),
        ...(responsesMap?.[i] || {}),
      };
      for (const k of keys) {
        const rec = stepResponses[k];
        if (rec) {
          const rt = normalizeResponseType(rec.responseType);
          if (rt === LEAD_RESPONSE_TYPES.INTERESTED || rt === LEAD_RESPONSE_TYPES.NOT_INTERESTED) {
            hasExplicitPriorPositiveOrNegative = true;
          } else if (rt === LEAD_RESPONSE_TYPES.NO_REPLY) {
            hasExplicitPriorNoReply = true;
          }
        }
      }
    }

    if (hasExplicitPriorPositiveOrNegative) return true;
    // If the prior step explicitly has no_reply recorded, do NOT stop this lead for this step
    if (hasExplicitPriorNoReply) return false;

    // Helper to check if lead has recorded response at or after stepIdx
    const hasResponseAtOrAfter = () => {
      for (let i = stepIdx; i < steps.length; i++) {
        const stepResponses = {
          ...(steps[i]?.leadResponses || {}),
          ...(responsesMap?.[i] || {}),
        };
        for (const k of keys) {
          const rec = stepResponses[k];
          if (rec) {
            const rt = normalizeResponseType(rec.responseType);
            if (
              rt === LEAD_RESPONSE_TYPES.INTERESTED ||
              rt === LEAD_RESPONSE_TYPES.NOT_INTERESTED
            ) {
              return true;
            }
          }
        }
      }
      return false;
    };

    // 2. Check if lead was marked as lost/qualified or had a leadResponse at the lead level
    if (stage === 'lost' || stage === 'qualified') {
      if (hasResponseAtOrAfter()) {
        return false;
      }
      return true;
    }

    const leadRt = normalizeResponseType(lead.leadResponse);
    if (
      leadRt === LEAD_RESPONSE_TYPES.INTERESTED ||
      leadRt === LEAD_RESPONSE_TYPES.NOT_INTERESTED
    ) {
      if (hasResponseAtOrAfter()) {
        return false;
      }
      return true;
    }

    return false;
  };

  // ── Active Lead Cohort Timezone Summary ──
  const cohortTimezoneSummary = useMemo(() => {
    const unreplied = effectiveLeads.filter(
      (lead) => !hasLeadReplied(lead, formData?.journey, leadResponsesMap)
    );
    const activeLeads = unreplied.length > 0 ? unreplied : effectiveLeads;
    return resolveCohortTimezoneSummary(activeLeads, timingConfig);
  }, [effectiveLeads, formData?.journey, leadResponsesMap, timingConfig]);

  // ── Helper to determine if a step is blocked by Max Campaign Touches guardrail ──
  const isStepBlockedByCap = (item, idx) => {
    const sim = simulatedCadence.find((s) => s.stepNumber === item?.step) || simulatedCadence[idx];
    if (sim && sim.isBlocked) return true;
    const maxTouches = Number(timingConfig.maxCampaignTouches);
    return maxTouches > 0 && idx >= maxTouches;
  };

  // ── Compute Step Status Based on Delivery, Reply Cadence Stopping, and Nominal Arrival ──
  const computeStepStatus = (item, idx) => {
    // Rule 0: if step is blocked by max campaign touches limit, mark blocked
    if (isStepBlockedByCap(item, idx)) {
      return 'blocked';
    }

    // Check manual override first (persists user's manual choice unless changed)
    if (item.isManualStatusOverride && item.status) {
      return item.status;
    }

    const deliveryForStep = {
      ...(item.leadOutreachStatus || {}),
      ...(leadDeliveryMap[idx] || {}),
    };

    // Filter leads that have NOT replied prior to this step (replied leads are stopped from next steps)
    const stepLeads = effectiveLeads.filter(
      (lead) => !hasLeadRepliedBeforeStep(lead, idx, formData?.journey, leadResponsesMap)
    );
    const totalStepLeads = stepLeads.length;

    const unrepliedInStep = stepLeads.filter(
      (lead) => !hasLeadReplied(lead, formData?.journey, leadResponsesMap)
    );

    const sentCount = stepLeads.filter((lead) => isLeadSent(lead, deliveryForStep)).length;

    // Rule 1: when all eligible leads for this step have been sent (or if no unreplied leads remain), auto mark completed
    if (effectiveLeads.length > 0 && (totalStepLeads === 0 || unrepliedInStep.length === 0)) {
      return 'completed';
    }

    if (totalStepLeads > 0 && sentCount >= totalStepLeads) {
      return 'completed';
    }

    if (item.status === 'completed' && effectiveLeads.length === 0) {
      return 'completed';
    }

    const targetTz = timingConfig.fixedTimezone || 'Asia/Kuala_Lumpur';
    const stepDate = item.nominalDate || computeDefaultNominalDate(item.day);
    const stepTime = item.nominalTime || timingConfig.sendWindowStart || '09:00';
    const now = Date.now();

    const scheduledTime = parseDateTimeInTimezone(stepDate, stepTime, targetTz).getTime();
    // Rule 2: if the nominal plan still not arrive then it is not yet
    if (now < scheduledTime) {
      return 'not yet';
    }

    // Rule 3: if it is on the day or after but still not all eligible leads sent then it is pending
    return 'pending';
  };

  // ── Periodic Schedule Arrival Ticker (Triggers vibration/jump cues without auto popup) ──
  useEffect(() => {
    const intervalId = setInterval(() => {
      setCurrentScheduleTimestamp(Date.now());
    }, 2000);
    return () => clearInterval(intervalId);
  }, []);

  // ── First Pending Step Index (Only this single first pending step will vibrate) ──
  const firstPendingStepIndex = useMemo(() => {
    if (!Array.isArray(formData?.journey) || formData.journey.length === 0) return null;

    for (let idx = 0; idx < formData.journey.length; idx++) {
      const item = formData.journey[idx];
      if (!item) continue;
      if (isStepBlockedByCap(item, idx)) continue;

      const stepDeliveryMap = {
        ...(item.leadOutreachStatus || {}),
        ...(leadDeliveryMap[idx] || {}),
      };
      const stepLeads = effectiveLeads.filter(
        (lead) => !hasLeadRepliedBeforeStep(lead, idx, formData?.journey, leadResponsesMap)
      );
      const totalStepLeads = stepLeads.length;
      const sentCount = stepLeads.filter((l) => isLeadSent(l, stepDeliveryMap)).length;
      const isAllSent = totalStepLeads > 0 && sentCount >= totalStepLeads;
      if (isAllSent) continue;

      const stepCurrentStatus = computeStepStatus(item, idx);
      if (stepCurrentStatus === 'completed') continue;

      const isPending =
        stepCurrentStatus === 'pending' ||
        item.status === 'pending' ||
        (sentCount > 0 && !isAllSent);

      const hasWorkRemaining = effectiveLeads.length === 0 || stepLeads.length > 0;

      if (isPending && hasWorkRemaining) {
        return idx;
      }
    }

    return null;
  }, [
    formData?.journey,
    leadDeliveryMap,
    effectiveLeads,
    leadResponsesMap,
    simulatedCadence,
    timingConfig,
    currentScheduleTimestamp,
  ]);

  // ── Helper to determine if a step is the single active pending step needing attention ──
  const isStepArrivedAttention = (item, idx) => {
    if (idx !== firstPendingStepIndex) return false;
    if (clickedAttentionSteps.has(idx)) return false;
    return true;
  };

  // ── Sync Lead Pipeline Stage to 'Contacted' for Leads that Launched First Outreach ──
  useEffect(() => {
    if (!Array.isArray(formData?.journey) || formData.journey.length === 0) return;
    if (!Array.isArray(effectiveLeads) || effectiveLeads.length === 0) return;

    const leadsToContact = [];
    const leadUpdates = {};

    effectiveLeads.forEach((lead) => {
      const currentStage = String(lead.stage || '').toLowerCase();
      // Only transition leads that are in 'New' / uncontacted stage
      if (currentStage !== 'new' && lead.stage) return;

      const isSentInAnyStep = formData.journey.some((step, idx) => {
        const delivery = {
          ...(step?.leadOutreachStatus || {}),
          ...(leadDeliveryMap[idx] || {}),
        };
        return isLeadSent(lead, delivery);
      });

      if (isSentInAnyStep) {
        leadsToContact.push(lead);
        getLeadKeys(lead).forEach((k) => {
          leadUpdates[k] = { stage: 'Contacted' };
        });
      }
    });

    if (leadsToContact.length === 0) return;

    updateLeadsInFormData(
      (l) => leadsToContact.some((tc) => getLeadKeys(tc).some((k) => getLeadKeys(l).includes(k))),
      { stage: 'Contacted' }
    );

    leadsToContact.forEach((lead) => {
      const crmId = lead.pipelineLeadId || lead.id || lead._id;
      safePatchLeadStage(crmId, 'Contacted');
    });

    if (typeof onPersistTouchpointStatus === 'function') {
      onPersistTouchpointStatus(0, { leadUpdates });
    }
  }, [formData?.journey, leadDeliveryMap, effectiveLeads]);

  // ── Guardrails Validation (Pacing, Weekend, Sending Window, Campaign Duration) ──
  const validateStepSchedule = (stepIdx, proposedDate, proposedTime, journeyList) => {
    const violations = [];
    const targetTz = timingConfig.fixedTimezone || 'Asia/Kuala_Lumpur';
    const timeStr = proposedTime || timingConfig.sendWindowStart || '09:00';
    const targetDt = parseDateTimeInTimezone(proposedDate, timeStr, targetTz);

    // 0. Campaign Window Duration Constraints (Set in Step 1)
    let isDurationViolation = false;
    if (campaignStartDate && proposedDate < campaignStartDate) {
      isDurationViolation = true;
      violations.push(
        `Cannot be scheduled before campaign start date (${campaignStartDate}). All cadence steps must occur within the campaign duration.`
      );
    }
    if (campaignEndDate && proposedDate > campaignEndDate) {
      isDurationViolation = true;
      violations.push(
        `Cannot be scheduled after campaign end date (${campaignEndDate}). The campaign duration set in Step 1 is ${campaignDurationDays} days.`
      );
    }

    if (stepIdx > 0) {
      const prevStep = journeyList[stepIdx - 1];
      const prevDate = prevStep.nominalDate || computeDefaultNominalDate(prevStep.day);
      const prevTime = prevStep.nominalTime || timingConfig.sendWindowStart || '09:00';
      const prevDt = parseDateTimeInTimezone(prevDate, prevTime, targetTz);

      const diffHours = (targetDt.getTime() - prevDt.getTime()) / (3600 * 1000);

      if (diffHours <= 0) {
        violations.push(
          `Cannot be scheduled before or at the same time as Step ${prevStep.step} (${prevDate} ${prevTime}).`
        );
      } else if (diffHours < (timingConfig.minDelayHours || 48)) {
        violations.push(
          `Scheduled only ${Math.round(diffHours)} hours after Step ${prevStep.step}. Minimum delay rule requires at least ${timingConfig.minDelayHours || 48} hours between outreach touches.`
        );
      }
    }

    const parts = getDatePartsInTimezone(targetDt, targetTz);
    const dayOfWeek = parts ? parts.dayOfWeek : targetDt.getDay();
    const isoDay = dayOfWeek === 0 ? 7 : dayOfWeek;
    const allowedDays = Array.isArray(timingConfig.businessDays)
      ? timingConfig.businessDays
      : [1, 2, 3, 4, 5];

    if (timingConfig.excludeWeekends && (isoDay === 6 || isoDay === 7)) {
      violations.push(
        'Lands on a weekend (Saturday/Sunday) while "Skip Weekends" protection is enabled.'
      );
    } else if (!allowedDays.includes(isoDay)) {
      violations.push('Lands on a non-business day based on campaign business hour guardrails.');
    }

    const [targetH, targetM] = timeStr.split(':').map(Number);
    const targetMinutes = targetH * 60 + (targetM || 0);
    const [startH, startM] = (timingConfig.sendWindowStart || '09:00').split(':').map(Number);
    const startMinutes = startH * 60 + (startM || 0);
    const [endH, endM] = (timingConfig.sendWindowEnd || '18:00').split(':').map(Number);
    const endMinutes = endH * 60 + (endM || 0);

    if (targetMinutes < startMinutes || targetMinutes > endMinutes) {
      violations.push(
        `Time ${timeStr} is outside the allowed sending window (${timingConfig.sendWindowStart || '09:00'} - ${timingConfig.sendWindowEnd || '18:00'}).`
      );
    }

    let candidateRecDt = new Date(targetDt.getTime());
    if (stepIdx > 0) {
      const prevStep = journeyList[stepIdx - 1];
      const prevDate = prevStep.nominalDate || computeDefaultNominalDate(prevStep.day);
      const prevTime = prevStep.nominalTime || timingConfig.sendWindowStart || '09:00';
      const prevDt = parseDateTimeInTimezone(prevDate, prevTime, targetTz);
      const minDelayMs = (timingConfig.minDelayHours || 48) * 3600 * 1000;
      if (candidateRecDt.getTime() < prevDt.getTime() + minDelayMs) {
        candidateRecDt = new Date(prevDt.getTime() + minDelayMs);
      }
    }

    const recEligible = calculateNextEligibleSendTime({
      targetTime: candidateRecDt,
      prospectTimezone: targetTz,
      config: timingConfig,
    });

    const recDt = recEligible.nextEligibleTime || candidateRecDt;
    const recParts = getDatePartsInTimezone(recDt, targetTz);
    const recDateStr = recParts ? recParts.dateKey : formatLocalDate(recDt);
    const recTimeStr = timingConfig.sendWindowStart || '09:00';
    const exceedsCampaignWindow = Boolean(campaignEndDate && recDateStr > campaignEndDate);
    if (exceedsCampaignWindow && !isDurationViolation) {
      violations.push(
        `The required cadence pacing pushes this touchpoint to ${recDateStr}, which exceeds the campaign end date (${campaignEndDate}). Adjust prior step timing or extend campaign duration in Step 1.`
      );
    }
    const recDay = computeDayFromDate(recDateStr);

    return {
      isValid: violations.length === 0,
      violations,
      recommendedDate: recDateStr,
      recommendedTime: recTimeStr,
      recommendedDay: recDay,
      isDurationViolation,
      exceedsCampaignWindow,
    };
  };

  // ── Apply Step Date and Ripple Subsequent Steps Pacing ──
  const applyStepDateAndRipple = (stepIdx, newDate, newTime, journeyList, customConfig = null) => {
    const activeConfig = customConfig || timingConfig;
    const targetTz = activeConfig.fixedTimezone || 'Asia/Kuala_Lumpur';
    const updatedJourney = [...journeyList];
    const newDay = computeDayFromDate(newDate);

    updatedJourney[stepIdx] = {
      ...updatedJourney[stepIdx],
      nominalDate: newDate,
      nominalTime: newTime,
      day: newDay,
      isExceedingDuration: Boolean(campaignEndDate && newDate > campaignEndDate),
    };

    for (let i = stepIdx + 1; i < updatedJourney.length; i++) {
      const prevStep = updatedJourney[i - 1];
      const prevDateStr = prevStep.nominalDate || computeDefaultNominalDate(prevStep.day);
      const prevTimeStr = prevStep.nominalTime || activeConfig.sendWindowStart || '09:00';
      const prevDt = parseDateTimeInTimezone(prevDateStr, prevTimeStr, targetTz);

      const minDelayMs = (activeConfig.minDelayHours || 48) * 3600 * 1000;
      const candidateDt = new Date(prevDt.getTime() + minDelayMs);

      const eligible = calculateNextEligibleSendTime({
        targetTime: candidateDt,
        prospectTimezone: targetTz,
        config: activeConfig,
      });

      const nextDt = eligible.nextEligibleTime || candidateDt;
      const parts = getDatePartsInTimezone(nextDt, targetTz);
      const nextDateStr = parts ? parts.dateKey : formatLocalDate(nextDt);
      const nextTimeStr = prevTimeStr || activeConfig.sendWindowStart || '09:00';
      const nextDay = computeDayFromDate(nextDateStr);

      updatedJourney[i] = {
        ...updatedJourney[i],
        nominalDate: nextDateStr,
        nominalTime: nextTimeStr,
        day: nextDay,
        isExceedingDuration: Boolean(campaignEndDate && nextDateStr > campaignEndDate),
      };
    }

    setFormData((prev) => ({
      ...prev,
      schedule: activeConfig,
      journey: updatedJourney,
      strategy: {
        ...(prev?.strategy || {}),
        schedule: activeConfig,
        journey: updatedJourney,
      },
    }));
  };

  const handleIgnoreSendingWindowWarning = () => {
    const { stepIdx, proposedTime, currentDate, isBeforeStart, isAfterEnd } =
      sendingWindowWarningModal;

    setSendingWindowWarningModal((prev) => ({ ...prev, isOpen: false }));

    const finalStart = isBeforeStart ? proposedTime : timingConfig.sendWindowStart || '09:00';
    const finalEnd = isAfterEnd ? proposedTime : timingConfig.sendWindowEnd || '18:00';
    const qStart = parseTimeToMinutes(timingConfig.quietHoursStart || '18:00');
    const qEnd = parseTimeToMinutes(timingConfig.quietHoursEnd || '09:00');
    const sStart = parseTimeToMinutes(finalStart);
    const sEnd = parseTimeToMinutes(finalEnd);

    const isViolatingQuietHours =
      qStart > qEnd ? sStart < qEnd || sEnd > qStart : sStart < qEnd && sEnd > qStart;

    const nextTimingConfig = {
      ...timingConfig,
      sendWindowStart: finalStart,
      sendWindowEnd: finalEnd,
      ...(isViolatingQuietHours ? { quietHoursEnabled: false } : {}),
    };

    applyStepDateAndRipple(
      stepIdx,
      currentDate,
      proposedTime,
      formData.journey || [],
      nextTimingConfig
    );

    scrollToSendingWindow();
  };

  const handleStepDateChange = (stepIdx, newDate) => {
    if (isReadOnly || !newDate) return;

    // For the first step, enforce that the date is within campaign bounds
    let effectiveDate = newDate;
    if (stepIdx === 0 && campaignStartDate && effectiveDate < campaignStartDate) {
      effectiveDate = campaignStartDate;
    }

    const currentStep = formData.journey[stepIdx];
    const currentTime = currentStep.nominalTime || timingConfig.sendWindowStart || '09:00';

    const check = validateStepSchedule(stepIdx, effectiveDate, currentTime, formData.journey);
    if (!check.isValid) {
      setGuardrailModal({
        isOpen: true,
        stepIdx,
        proposedDate: effectiveDate,
        proposedTime: currentTime,
        proposedDay: computeDayFromDate(effectiveDate),
        violations: check.violations,
        recommendedDate: check.recommendedDate,
        recommendedTime: check.recommendedTime,
        recommendedDay: check.recommendedDay,
        isDurationViolation: check.isDurationViolation,
        exceedsCampaignWindow: check.exceedsCampaignWindow,
      });
      return;
    }

    applyStepDateAndRipple(stepIdx, effectiveDate, currentTime, formData.journey);
  };

  const handleStepTimeChange = (stepIdx, newTime) => {
    if (isReadOnly || !newTime) return;
    const currentStep = formData.journey[stepIdx];
    const currentDate = currentStep.nominalDate || computeDefaultNominalDate(currentStep.day);

    const windowStart = timingConfig.sendWindowStart || '09:00';
    const windowEnd = timingConfig.sendWindowEnd || '18:00';
    const newMinutes = parseTimeToMinutes(newTime);
    const startMinutes = parseTimeToMinutes(windowStart);
    const endMinutes = parseTimeToMinutes(windowEnd);

    const isBeforeStart = newMinutes < startMinutes;
    const isAfterEnd = newMinutes > endMinutes;

    if (isBeforeStart || isAfterEnd) {
      setSendingWindowWarningModal({
        isOpen: true,
        stepIdx,
        proposedTime: newTime,
        currentDate,
        currentWindowStart: windowStart,
        currentWindowEnd: windowEnd,
        newProposedStart: isBeforeStart ? newTime : windowStart,
        newProposedEnd: isAfterEnd ? newTime : windowEnd,
        isBeforeStart,
        isAfterEnd,
        message: `Must follow the allow sending window (${windowStart} – ${windowEnd}).`,
      });
      scrollToSendingWindow();
      return;
    }

    const check = validateStepSchedule(stepIdx, currentDate, newTime, formData.journey);
    if (!check.isValid) {
      setGuardrailModal({
        isOpen: true,
        stepIdx,
        proposedDate: currentDate,
        proposedTime: newTime,
        proposedDay: computeDayFromDate(currentDate),
        violations: check.violations,
        recommendedDate: check.recommendedDate,
        recommendedTime: check.recommendedTime,
        recommendedDay: check.recommendedDay,
        isDurationViolation: check.isDurationViolation,
        exceedsCampaignWindow: check.exceedsCampaignWindow,
      });
      return;
    }

    applyStepDateAndRipple(stepIdx, currentDate, newTime, formData.journey);
  };

  const handleDayInputChange = (stepIdx, newDay) => {
    if (isReadOnly) return;
    const clampedDay = Math.max(1, Math.min(maxCampaignDay, parseInt(newDay, 10) || 1));
    const calculatedDate = computeDefaultNominalDate(clampedDay);
    handleStepDateChange(stepIdx, calculatedDate);
  };

  // ── Lead Delivery & Response Recording Handlers ──
  const handleToggleLeadSend = (stepIdx, leadKey) => {
    if (isReadOnly) return;
    const currentStep = formData.journey[stepIdx] || {};
    const existingDelivery = {
      ...(currentStep.leadOutreachStatus || {}),
      ...(leadDeliveryMap[stepIdx] || {}),
    };

    // Find matching lead
    const matchedLead = effectiveLeads.find((l) => {
      const keys = getLeadKeys(l);
      return keys.includes(leadKey);
    });

    const isCurrentlySent = matchedLead
      ? isLeadSent(matchedLead, existingDelivery)
      : existingDelivery[leadKey]?.sent === true;
    const nextSent = !isCurrentlySent;
    const nowTimeStr = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

    const nextDelivery = { ...existingDelivery };
    const targetKeys = matchedLead ? getLeadKeys(matchedLead) : [leadKey];
    targetKeys.forEach((k) => {
      nextDelivery[k] = {
        sent: nextSent,
        sentAt: nextSent ? existingDelivery[k]?.sentAt || nowTimeStr : null,
      };
    });

    setLeadDeliveryMap((prev) => ({
      ...prev,
      [stepIdx]: nextDelivery,
    }));

    onUpdateJourneyStep(stepIdx, 'leadOutreachStatus', nextDelivery);

    const stepLeads = effectiveLeads.filter(
      (lead) => !hasLeadRepliedBeforeStep(lead, stepIdx, formData?.journey, leadResponsesMap)
    );
    const totalLeads = stepLeads.length;
    const sentCount = stepLeads.filter((l) => isLeadSent(l, nextDelivery)).length;

    if (totalLeads > 0 && sentCount >= totalLeads) {
      onUpdateJourneyStep(stepIdx, 'status', 'completed');
    }

    let leadUpdateObj = null;

    if (nextSent) {
      // First outreach launched: if lead is in stage 'New' (or not yet contacted), advance to 'Contacted'
      const currentStage = String(matchedLead?.stage || '').toLowerCase();
      if (!matchedLead?.stage || currentStage === 'new') {
        leadUpdateObj = { stage: 'Contacted' };
        updateLeadsInFormData(targetKeys, leadUpdateObj);

        const crmId = matchedLead?.pipelineLeadId || matchedLead?.id || matchedLead?._id;
        safePatchLeadStage(crmId, 'Contacted');
      }
    } else {
      // Toggled off: check if lead has been sent outreach in ANY other journey step
      const isSentInOtherSteps = (formData?.journey || []).some((step, sIdx) => {
        if (sIdx === stepIdx) return false;
        const stepDelivery = {
          ...(step?.leadOutreachStatus || {}),
          ...(leadDeliveryMap[sIdx] || {}),
        };
        return isLeadSent(matchedLead, stepDelivery);
      });

      // If lead is not sent in any other step and its stage is 'Contacted', revert to 'New'
      if (!isSentInOtherSteps && String(matchedLead?.stage || '').toLowerCase() === 'contacted') {
        leadUpdateObj = { stage: 'New' };
        updateLeadsInFormData(targetKeys, leadUpdateObj);

        const crmId = matchedLead?.pipelineLeadId || matchedLead?.id || matchedLead?._id;
        safePatchLeadStage(crmId, 'New');
      }
    }

    if (typeof onPersistTouchpointStatus === 'function') {
      const stepStatus = totalLeads > 0 && sentCount >= totalLeads ? 'completed' : undefined;
      onPersistTouchpointStatus(stepIdx, {
        leadOutreachStatus: nextDelivery,
        ...(stepStatus ? { status: stepStatus } : {}),
        ...(leadUpdateObj ? { leadUpdates: { [leadKey]: leadUpdateObj } } : {}),
      });
    }
  };

  const handleMarkAllLeads = (stepIdx, shouldBeSent) => {
    if (isReadOnly) return;
    const currentStep = formData.journey[stepIdx] || {};
    const stepLeads = effectiveLeads.filter(
      (lead) => !hasLeadRepliedBeforeStep(lead, stepIdx, formData?.journey, leadResponsesMap)
    );
    const existingDelivery = {
      ...(currentStep.leadOutreachStatus || {}),
      ...(leadDeliveryMap[stepIdx] || {}),
    };
    const nextDelivery = { ...existingDelivery };
    const nowTimeStr = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

    stepLeads.forEach((l) => {
      const keys = getLeadKeys(l);
      keys.forEach((k) => {
        nextDelivery[k] = {
          sent: shouldBeSent,
          sentAt: shouldBeSent ? nowTimeStr : null,
        };
      });
    });

    setLeadDeliveryMap((prev) => ({
      ...prev,
      [stepIdx]: nextDelivery,
    }));

    onUpdateJourneyStep(stepIdx, 'leadOutreachStatus', nextDelivery);

    let nextStatus;
    if (shouldBeSent) {
      nextStatus = 'completed';
      onUpdateJourneyStep(stepIdx, 'status', 'completed');
    } else {
      const stepDate = currentStep.nominalDate || computeDefaultNominalDate(currentStep.day);
      const stepTime = currentStep.nominalTime || timingConfig.sendWindowStart || '09:00';
      const scheduledTime = new Date(
        `${stepDate}T${stepTime.length === 5 ? `${stepTime}:00` : stepTime}`
      ).getTime();
      nextStatus = Date.now() < scheduledTime ? 'not yet' : 'pending';
      onUpdateJourneyStep(stepIdx, 'status', nextStatus);
    }

    const leadUpdates = {};
    const targetLeadKeys = [];

    if (shouldBeSent) {
      stepLeads.forEach((l) => {
        const currentStage = String(l.stage || '').toLowerCase();
        if (!l.stage || currentStage === 'new') {
          const keys = getLeadKeys(l);
          keys.forEach((k) => {
            leadUpdates[k] = { stage: 'Contacted' };
            targetLeadKeys.push(k);
          });
          const crmId = l.pipelineLeadId || l.id || l._id;
          safePatchLeadStage(crmId, 'Contacted');
        }
      });
      if (targetLeadKeys.length > 0) {
        updateLeadsInFormData(targetLeadKeys, { stage: 'Contacted' });
      }
    } else {
      stepLeads.forEach((l) => {
        const isSentInOtherSteps = (formData?.journey || []).some((step, sIdx) => {
          if (sIdx === stepIdx) return false;
          const stepDelivery = {
            ...(step?.leadOutreachStatus || {}),
            ...(leadDeliveryMap[sIdx] || {}),
          };
          return isLeadSent(l, stepDelivery);
        });

        if (!isSentInOtherSteps && String(l.stage || '').toLowerCase() === 'contacted') {
          const keys = getLeadKeys(l);
          keys.forEach((k) => {
            leadUpdates[k] = { stage: 'New' };
            targetLeadKeys.push(k);
          });
          const crmId = l.pipelineLeadId || l.id || l._id;
          safePatchLeadStage(crmId, 'New');
        }
      });
      if (targetLeadKeys.length > 0) {
        updateLeadsInFormData(targetLeadKeys, { stage: 'New' });
      }
    }

    if (typeof onPersistTouchpointStatus === 'function') {
      onPersistTouchpointStatus(stepIdx, {
        leadOutreachStatus: nextDelivery,
        status: nextStatus,
        ...(Object.keys(leadUpdates).length > 0 ? { leadUpdates } : {}),
      });
    }
  };

  const handleConfirmSaveLeadInterested = async () => {
    const { stepIdx, leadKey, assignedMember, note, tasks, lead } = leadInterestedModal;
    if (stepIdx === null || !leadKey) return;

    const currentStep = formData.journey[stepIdx] || {};
    const existingResponses = leadResponsesMap[stepIdx] || currentStep.leadResponses || {};
    const nowTimeStr = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

    const selectedMemberObj = campaignMembers.find(
      (m) => String(m.userId || m.id) === String(assignedMember)
    );
    const assignedMemberName = selectedMemberObj
      ? selectedMemberObj.name || selectedMemberObj.email
      : assignedMember;
    const matchedLead = effectiveLeads.find((l) => getLeadKeys(l).includes(leadKey));
    const targetKeys = matchedLead ? getLeadKeys(matchedLead) : [leadKey];

    const nextResponses = { ...existingResponses };
    targetKeys.forEach((k) => {
      nextResponses[k] = {
        responseType: 'interested',
        note:
          note !== undefined
            ? extractNoteText(note)
            : extractNoteText(existingResponses[k]?.note) || '',
        respondedAt: nowTimeStr,
      };
    });

    setLeadResponsesMap((prev) => ({
      ...prev,
      [stepIdx]: nextResponses,
    }));

    onUpdateJourneyStep(stepIdx, 'leadResponses', nextResponses);

    const leadUpdateObj = {
      stage: 'Qualified',
      assignee: assignedMember || '',
      assigneeName: assignedMemberName || '',
      tasks: tasks || [],
      leadResponse: 'interested',
    };

    // Update local formData.importedLeads and strategy for all alias keys
    updateLeadsInFormData(targetKeys, leadUpdateObj);

    if (typeof onPersistTouchpointStatus === 'function') {
      const leadUpdatesMap = {};
      targetKeys.forEach((k) => {
        leadUpdatesMap[k] = leadUpdateObj;
      });
      onPersistTouchpointStatus(stepIdx, {
        leadResponses: nextResponses,
        leadUpdates: leadUpdatesMap,
      });
    }

    // Direct CRM sync if pipelineLeadId / lead id is available
    const crmId =
      matchedLead?.pipelineLeadId ||
      matchedLead?.id ||
      matchedLead?._id ||
      lead?.pipelineLeadId ||
      lead?.id ||
      lead?._id;
    if (crmId) {
      // 1. Update stage and assignee via PATCH (top-level keys so backend pickCrmMeta picks them up)
      try {
        await axios.patch(`/lead-pipeline/${crmId}`, {
          stage: 'Qualified',
          assignee: assignedMember || '',
          lostReason: null,
        });
      } catch (e) {
        console.warn('[Step4TeamAssign] Failed to update lead stage/assignee:', e);
      }

      // 2. Create each task via the dedicated tasks API
      if (Array.isArray(tasks) && tasks.length > 0) {
        for (const task of tasks) {
          try {
            await axios.post(`/lead-pipeline/${crmId}/tasks`, {
              title: task.title || '',
              description: task.description || '',
              dueDate: task.dueDate || '',
              priority: task.priority || 'Medium',
              assignee: task.assignee || assignedMember || '',
              status: task.status || 'pending',
            });
          } catch (taskErr) {
            console.warn('[Step4TeamAssign] Failed to create task:', taskErr);
          }
        }
      }

      // 3. Create the note via the dedicated notes API
      if (note && note.trim()) {
        try {
          await axios.post(`/lead-pipeline/${crmId}/notes`, {
            text: note.trim(),
          });
        } catch (noteErr) {
          console.warn('[Step4TeamAssign] Failed to create note:', noteErr);
        }
      }
    }

    setLeadInterestedModal({
      isOpen: false,
      stepIdx: null,
      lead: null,
      leadKey: '',
      assignedMember: '',
      note: '',
      tasks: [],
      taskInput: '',
      taskDueDate: '',
      taskPriority: 'Medium',
      taskAssignee: '',
    });
  };

  const handleUpdateLeadResponse = (stepIdx, leadKey, responseType, note) => {
    if (isReadOnly) return;
    const currentStep = formData.journey[stepIdx] || {};
    const existingResponses = leadResponsesMap[stepIdx] || currentStep.leadResponses || {};
    const nowTimeStr = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

    const normalizedType =
      responseType === 'positive'
        ? 'interested'
        : responseType === 'negative'
          ? 'not_interested'
          : responseType;

    // If positive / interested, pop out the "Lead interested" modal
    if (normalizedType === 'interested') {
      const targetLead = effectiveLeads.find((l) => getLeadKeys(l).includes(leadKey));
      setLeadInterestedModal({
        isOpen: true,
        stepIdx,
        lead: targetLead || { id: leadKey, name: leadKey },
        leadKey,
        assignedMember:
          targetLead?.assignee ||
          (campaignMembers[0] ? campaignMembers[0].userId || campaignMembers[0].id : ''),
        note:
          note !== undefined
            ? extractNoteText(note)
            : extractNoteText(existingResponses[leadKey]?.note) || '',
        tasks: Array.isArray(targetLead?.tasks) ? [...targetLead.tasks] : [],
        taskInput: '',
        taskDueDate: '',
        taskPriority: 'Medium',
        taskAssignee:
          targetLead?.assignee ||
          (campaignMembers[0] ? campaignMembers[0].userId || campaignMembers[0].id : ''),
      });
      return;
    }

    const matchedLead = effectiveLeads.find((l) => getLeadKeys(l).includes(leadKey));
    const targetKeys = matchedLead ? getLeadKeys(matchedLead) : [leadKey];

    const nextResponses = { ...existingResponses };
    targetKeys.forEach((k) => {
      nextResponses[k] = {
        responseType: normalizedType || existingResponses[k]?.responseType || 'no_reply',
        note:
          note !== undefined
            ? extractNoteText(note)
            : extractNoteText(existingResponses[k]?.note) || '',
        respondedAt: nowTimeStr,
      };
    });

    setLeadResponsesMap((prev) => ({
      ...prev,
      [stepIdx]: nextResponses,
    }));

    onUpdateJourneyStep(stepIdx, 'leadResponses', nextResponses);

    let leadUpdateObj = null;

    if (normalizedType === 'not_interested') {
      // Negative reply: Do NOT remove lead from campaign; update stage to 'Lost' & lostReason to 'Not interested'
      leadUpdateObj = {
        stage: 'Lost',
        lostReason: 'Not interested',
        leadResponse: 'not_interested',
      };
      updateLeadsInFormData(targetKeys, leadUpdateObj);
    } else if (normalizedType === 'no_reply') {
      // Check if any OTHER journey step currently has an active positive or negative response for this lead
      const hasOtherResponse = Object.entries(leadResponsesMap).some(
        ([otherStepIdx, responses]) => {
          if (String(otherStepIdx) === String(stepIdx)) return false;
          return targetKeys.some((k) => {
            const respType = responses?.[k]?.responseType;
            return respType === 'interested' || respType === 'not_interested';
          });
        }
      );

      if (!hasOtherResponse) {
        const stepDelivery = {
          ...(currentStep.leadOutreachStatus || {}),
          ...(leadDeliveryMap[stepIdx] || {}),
        };
        const isSent = matchedLead
          ? isLeadSent(matchedLead, stepDelivery)
          : targetKeys.some((k) => stepDelivery[k]?.sent === true);
        leadUpdateObj = {
          stage: isSent ? 'Contacted' : 'New',
          lostReason: null,
          leadResponse: 'no_reply',
        };
        updateLeadsInFormData(targetKeys, leadUpdateObj);
      }
    }

    if (typeof onPersistTouchpointStatus === 'function') {
      const leadUpdatesMap = {};
      if (leadUpdateObj) {
        targetKeys.forEach((k) => {
          leadUpdatesMap[k] = leadUpdateObj;
        });
      }
      onPersistTouchpointStatus(stepIdx, {
        leadResponses: nextResponses,
        ...(leadUpdateObj ? { leadUpdates: leadUpdatesMap } : {}),
      });
    }

    if (leadUpdateObj && (normalizedType === 'not_interested' || normalizedType === 'no_reply')) {
      const crmId = matchedLead?.pipelineLeadId || matchedLead?.id || matchedLead?._id;
      safePatchLeadStage(crmId, leadUpdateObj.stage, leadUpdateObj.lostReason);
    }
  };

  const handleManualStatusChange = (stepIdx, newStatus) => {
    if (isReadOnly) return;
    onUpdateJourneyStep(stepIdx, 'status', newStatus);
    onUpdateJourneyStep(stepIdx, 'isManualStatusOverride', true);

    if (typeof onPersistTouchpointStatus === 'function') {
      onPersistTouchpointStatus(stepIdx, {
        status: newStatus,
        isManualStatusOverride: true,
      });
    }
  };

  const handleGenerateAiSequence = async () => {
    if (isReadOnly || isGeneratingAi) return;
    setIsGeneratingAi(true);
    setAiError('');

    try {
      const res = await axios.post('/outreach/generate-sequence', {
        title: formData.title,
        product: formData.product,
        productDescription: formData.productDescription,
        primaryObjective: formData.primaryObjective,
        industrySector: formData.industrySector,
        targetRegion: formData.targetRegion,
        targetPersonas: formData.targetPersonas,
        channels: formData.channels || ['Email', 'LinkedIn'],
        durationDays: campaignDurationDays,
        maxTouches: timingConfig.maxCampaignTouches || 5,
        startDate: campaignStartDate,
        endDate: campaignEndDate,
        positioning: formData.positioning,
        painPoints: formData.painPoints,
        matrix: formData.matrix,
        reachabilityStats,
        consentBasis: 'B2B Legitimate Interest / PDPA Inquirer Consent',
        availableIntegrations: {
          email: true,
          linkedIn: true,
          whatsApp: true,
          phoneCall: true,
        },
      });

      if (
        res.data?.success &&
        Array.isArray(res.data?.data?.journey) &&
        res.data.data.journey.length > 0
      ) {
        const targetTz = timingConfig.fixedTimezone || 'Asia/Kuala_Lumpur';
        const alignedJourney = alignJourneyToTimingGuardrails({
          journey: res.data.data.journey,
          startDate: campaignStartDate,
          endDate: campaignEndDate,
          config: timingConfig,
          timezone: targetTz,
          filterToDuration: true,
        });
        setFormData((prev) => ({
          ...prev,
          journey: alignedJourney,
          strategy: {
            ...(prev?.strategy || {}),
            journey: alignedJourney,
          },
        }));
      } else {
        handleGenerateDefaultSequence();
      }
      if (onGeneratedSequence) onGeneratedSequence();
    } catch (err) {
      console.warn('[Step4TeamAssign] AI sequence generation error, using fallback:', err);
      handleGenerateDefaultSequence();
      if (onGeneratedSequence) onGeneratedSequence();
    } finally {
      setIsGeneratingAi(false);
    }
  };

  const handleGenerateDefaultSequence = () => {
    if (isReadOnly) return;
    const prod = formData.product || 'the solution';
    const chs =
      formData.channels && formData.channels.length > 0 ? formData.channels : ['Email', 'LinkedIn'];
    const duration = campaignDurationDays;
    const maxTouches = Math.max(1, Number(timingConfig.maxCampaignTouches) || 5);
    const naturalStepCount = duration <= 14 ? 3 : duration <= 30 ? 4 : duration <= 60 ? 5 : 6;
    const stepCount = Math.min(maxTouches, naturalStepCount);
    const interval = Math.max(2, Math.floor((duration - 1) / Math.max(stepCount - 1, 1)));

    const defaultNames = [
      `Value Hook & Introduction to ${prod}`,
      `Executive Connection & Relevant Case Study`,
      formData.positioning
        ? `Verified Proof: ${formData.positioning.slice(0, 50)}...`
        : 'Verified Proof Points & Operational ROI Benchmark',
      '15-Minute Executive Consultation & Solution Walkthrough',
      'Peer Benchmark Insights & Value Assessment',
      'Final Follow-up & Industry Benchmark Report',
    ];

    const journey = [];
    for (let i = 0; i < stepCount; i++) {
      const primaryCh = chs[i % chs.length];
      const fallbackCh = chs.find((c) => c !== primaryCh) || 'Auto';
      const stepDay = i === 0 ? 1 : Math.min(duration, 1 + i * interval);
      if (journey.length === 0 || stepDay > journey[journey.length - 1].day) {
        journey.push({
          step: journey.length + 1,
          day: stepDay,
          channel: primaryCh,
          fallbackChannel: fallbackCh,
          channelRationale: `Standard cadence pairing for ${primaryCh} with ${fallbackCh} fallback.`,
          name: defaultNames[i % defaultNames.length],
          condition: i === 0 ? 'Initial Touch' : 'If No Reply',
        });
      }
    }

    const targetTz = timingConfig.fixedTimezone || 'Asia/Kuala_Lumpur';
    const alignedJourney = alignJourneyToTimingGuardrails({
      journey,
      startDate: campaignStartDate,
      endDate: campaignEndDate,
      config: timingConfig,
      timezone: targetTz,
      filterToDuration: true,
    });

    setFormData((prev) => ({
      ...prev,
      journey: alignedJourney,
      strategy: {
        ...(prev?.strategy || {}),
        journey: alignedJourney,
      },
    }));
  };

  return (
    <div className={`cmp-blueprint-step-pane ${isReadOnly ? 'read-only' : ''}`}>
      <div className="cmp-blueprint-tag-pill">STEP 4 OF 6 • MULTI-TOUCH CADENCE & SEQUENCE</div>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '10px',
        }}
      >
        <div>
          <h2 className="cmp-blueprint-heading">Outreach Cadence Sequence & Channel Cadence</h2>
          <p className="cmp-blueprint-subheading">
            Multi-touch sequence across {campaignDurationDays} days ({campaignStartDate} to{' '}
            {campaignEndDate || 'End Date'}) with automated branching and quiet-hour controls.
          </p>
        </div>
        {!isReadOnly && formData.journey.length > 0 && (
          <button
            type="button"
            className="cmp-btn-primary"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              pointerEvents: isGeneratingAi ? 'none' : 'auto',
              cursor: isGeneratingAi ? 'not-allowed' : 'pointer',
            }}
            onClick={handleGenerateAiSequence}
            disabled={isGeneratingAi}
            title={
              hasGeneratedSequence
                ? 'Regenerate a bespoke multi-channel cadence sequence derived from your Brief, Personas, and Messaging Matrix'
                : 'Generate a bespoke multi-channel cadence sequence derived from your Brief, Personas, and Messaging Matrix'
            }
          >
            {isGeneratingAi ? (
              <>
                <i className="fas fa-spinner fa-spin" /> Synthesizing Cadence...
              </>
            ) : hasGeneratedSequence ? (
              <>
                <i className="fas fa-sync-alt" /> Regenerate Cadence with AI
              </>
            ) : (
              <>
                <i className="fas fa-magic" /> Generate Cadence with AI
              </>
            )}
          </button>
        )}
      </div>

      {aiError && (
        <div className="cmp-blueprint-error-box" style={{ marginBottom: '16px' }}>
          <i className="fas fa-exclamation-triangle" /> {aiError}
        </div>
      )}

      {hasAnyStepExceedingDuration && (
        <div
          className="cmp-guardrail-warning-box"
          style={{
            margin: '0 0 16px 0',
            background: '#fef2f2',
            border: '1px solid #f87171',
            color: '#991b1b',
            padding: '12px 16px',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
          }}
        >
          <i
            className="fas fa-exclamation-triangle"
            style={{ fontSize: '20px', flexShrink: 0, color: '#dc2626' }}
          />
          <div>
            <strong style={{ fontSize: '13px' }}>
              Cadence Schedule Exceeds Campaign Duration:
            </strong>
            <div style={{ fontSize: '12px', marginTop: '3px', color: '#7f1d1d' }}>
              One or more cadence steps are scheduled outside the campaign duration (
              {campaignStartDate} to {campaignEndDate}, {campaignDurationDays} days). All outreach
              touches must occur within the campaign duration configured in Step 1. Please adjust
              step days/dates or extend the campaign duration.
            </div>
          </div>
        </div>
      )}

      {/* ── COHORT CHANNEL REACHABILITY CARD ── */}
      {reachabilityStats.hasLeads ? (
        <div className="cmp-cohort-reachability-card">
          <div className="cmp-reachability-header">
            <div className="cmp-reachability-title-row">
              <div className="cmp-reachability-badge-icon">
                <i className="fas fa-satellite-dish" />
              </div>
              <div>
                <h4 className="cmp-reachability-heading">Imported Cohort Channel Reachability</h4>
                <p className="cmp-reachability-subheading">
                  Contact channel coverage for <strong>{reachabilityStats.total}</strong> leads
                  imported from Step 2:
                </p>
              </div>
            </div>
            <div className="cmp-reachability-tags-summary">
              <span className="cmp-reachability-tag total">
                <i className="fas fa-users" /> {reachabilityStats.total} Total Leads
              </span>
              <span className="cmp-reachability-tag any">
                <i className="fas fa-check-circle text-success" />{' '}
                {reachabilityStats.anyChannelCount} Reachable (
                {Math.round((reachabilityStats.anyChannelCount / reachabilityStats.total) * 100)}%)
              </span>
              {reachabilityStats.missingAllCount > 0 && (
                <span className="cmp-reachability-tag warn">
                  <i className="fas fa-exclamation-circle text-warning" />{' '}
                  {reachabilityStats.missingAllCount} Missing All Channels
                </span>
              )}
            </div>
          </div>

          <div className="cmp-reachability-grid">
            <div className="cmp-reachability-card-item email">
              <div className="cmp-reach-card-top">
                <span className="cmp-reach-ch-name">
                  <i className="fas fa-envelope" /> Email Outreach
                </span>
                <strong className="cmp-reach-ch-pct">{reachabilityStats.email.percentage}%</strong>
              </div>
              <div className="cmp-reach-progress-track">
                <div
                  className="cmp-reach-progress-bar email"
                  style={{ width: `${reachabilityStats.email.percentage}%` }}
                />
              </div>
              <div className="cmp-reach-card-meta">
                <span>
                  {reachabilityStats.email.count} of {reachabilityStats.total} leads
                </span>
                {reachabilityStats.email.missing > 0 && (
                  <span className="cmp-reach-missing-label">
                    {reachabilityStats.email.missing} missing email
                  </span>
                )}
              </div>
            </div>

            <div className="cmp-reachability-card-item whatsapp">
              <div className="cmp-reach-card-top">
                <span className="cmp-reach-ch-name">
                  <i className="fab fa-whatsapp" /> WhatsApp Direct
                </span>
                <strong className="cmp-reach-ch-pct">
                  {reachabilityStats.whatsApp.percentage}%
                </strong>
              </div>
              <div className="cmp-reach-progress-track">
                <div
                  className="cmp-reach-progress-bar whatsapp"
                  style={{ width: `${reachabilityStats.whatsApp.percentage}%` }}
                />
              </div>
              <div className="cmp-reach-card-meta">
                <span>
                  {reachabilityStats.whatsApp.count} of {reachabilityStats.total} leads
                </span>
                {reachabilityStats.whatsApp.missing > 0 && (
                  <span className="cmp-reach-missing-label">
                    {reachabilityStats.whatsApp.missing} missing phone
                  </span>
                )}
              </div>
            </div>

            <div className="cmp-reachability-card-item linkedin">
              <div className="cmp-reach-card-top">
                <span className="cmp-reach-ch-name">
                  <i className="fab fa-linkedin-in" /> LinkedIn Profile
                </span>
                <strong className="cmp-reach-ch-pct">
                  {reachabilityStats.linkedIn.percentage}%
                </strong>
              </div>
              <div className="cmp-reach-progress-track">
                <div
                  className="cmp-reach-progress-bar linkedin"
                  style={{ width: `${reachabilityStats.linkedIn.percentage}%` }}
                />
              </div>
              <div className="cmp-reach-card-meta">
                <span>
                  {reachabilityStats.linkedIn.directCount} direct URLs ({reachabilityStats.total}{' '}
                  searchable)
                </span>
              </div>
            </div>

            <div className="cmp-reachability-card-item phone">
              <div className="cmp-reach-card-top">
                <span className="cmp-reach-ch-name">
                  <i className="fas fa-phone-alt" /> Sales Phone Call
                </span>
                <strong className="cmp-reach-ch-pct">
                  {reachabilityStats.phoneCall.percentage}%
                </strong>
              </div>
              <div className="cmp-reach-progress-track">
                <div
                  className="cmp-reach-progress-bar phone"
                  style={{ width: `${reachabilityStats.phoneCall.percentage}%` }}
                />
              </div>
              <div className="cmp-reach-card-meta">
                <span>
                  {reachabilityStats.phoneCall.count} of {reachabilityStats.total} leads
                </span>
                {reachabilityStats.phoneCall.missing > 0 && (
                  <span className="cmp-reach-missing-label">
                    {reachabilityStats.phoneCall.missing} missing phone
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="cmp-reachability-footer-hint">
            <i className="fas fa-info-circle" />
            <span>
              <strong>Outreach Strategy Tip:</strong> Each touchpoint defaults to its selected
              channel, but you can switch channels at any time inside the Outreach Launcher modal
              using the top tabs to contact leads with alternative channels.
            </span>
          </div>
        </div>
      ) : (
        <div className="cmp-cohort-reachability-card empty">
          <div className="cmp-reachability-header">
            <div className="cmp-reachability-title-row">
              <div
                className="cmp-reachability-badge-icon"
                style={{ background: '#f1f5f9', color: '#64748b' }}
              >
                <i className="fas fa-satellite-dish" />
              </div>
              <div>
                <h4 className="cmp-reachability-heading">Imported Cohort Channel Reachability</h4>
                <p className="cmp-reachability-subheading">
                  No decision-maker leads imported yet in Step 2. Once leads are added from
                  discovery or CSV, their email, WhatsApp, phone, and LinkedIn channel reachability
                  will be visualized here.
                </p>
              </div>
            </div>
            <div className="cmp-reachability-tags-summary">
              <span className="cmp-reachability-tag total">
                <i className="fas fa-users" /> 0 Total Leads
              </span>
            </div>
          </div>
        </div>
      )}

      {/* ── CAMPAIGN TEAM ROSTER SECTION ── */}
      {!isPersonalAccount && (
        <div className="cmp-campaign-team-card">
          <div className="cmp-team-card-header">
            <div className="cmp-team-card-title-wrap">
              <div className="cmp-team-badge-icon">
                <i className="fas fa-users-cog" />
              </div>
              <div>
                <h4 className="cmp-team-heading">Campaign Team Members</h4>
                <p className="cmp-team-subheading">
                  Team members assigned to execute and manage lead conversions for this campaign.
                  Converting an interested lead will assign them to one of these members.
                </p>
              </div>
            </div>
            {!isReadOnly && workspaceMembers.length > 0 && (
              <div className="cmp-team-add-control">
                <select
                  className="cmp-team-member-select"
                  value={selectedNewMemberId}
                  onChange={(e) => setSelectedNewMemberId(e.target.value)}
                >
                  <option value="">Select workspace member to add...</option>
                  {availableWorkspaceMembers.map((m) => (
                    <option key={m.user_id || m.id} value={m.user_id || m.id}>
                      {m.display_name || m.name || m.username || m.email} ({m.role || 'Member'})
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className="cmp-btn-team-add"
                  disabled={!selectedNewMemberId}
                  onClick={handleAddCampaignMember}
                >
                  <i className="fas fa-user-plus" /> Add to Campaign
                </button>
              </div>
            )}
          </div>

          <div className="cmp-team-members-list">
            {campaignMembers.length === 0 ? (
              <div className="cmp-team-empty-hint">
                <i className="fas fa-info-circle" /> No specific team members assigned to this
                campaign yet. All workspace reps can view this campaign.
              </div>
            ) : (
              campaignMembers.map((m) => {
                const memId = m.userId || m.id || String(m);
                const memName = m.name || m.email || String(m);
                const memRole = m.role || 'Campaign Rep';
                return (
                  <div key={memId} className="cmp-team-member-chip">
                    <div className="cmp-team-member-avatar">
                      <i className="fas fa-user" />
                    </div>
                    <div className="cmp-team-member-info">
                      <span className="cmp-team-member-name">{memName}</span>
                      <span className="cmp-team-member-role">{memRole}</span>
                    </div>
                    {!isReadOnly && (
                      <button
                        type="button"
                        className="cmp-team-remove-btn"
                        onClick={() => handleRemoveCampaignMember(memId)}
                        title={`Remove ${memName} from campaign`}
                      >
                        &times;
                      </button>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      <div>
        {formData.journey.length === 0 ? (
          <div className="cmp-empty-step-notice">
            <div className="cmp-empty-notice-left">
              <i className="fas fa-stream" />
              <div className="cmp-empty-notice-text">
                <strong>No Outreach Cadence Sequence Defined Yet</strong>
                <p>
                  Generate the recommended AI multi-channel sequence or add individual touchpoint
                  steps manually.
                </p>
              </div>
            </div>
            {!isReadOnly && (
              <div className="cmp-empty-actions-wrap">
                <button
                  type="button"
                  className="cmp-btn-primary"
                  onClick={handleGenerateAiSequence}
                  disabled={isGeneratingAi}
                  style={{
                    pointerEvents: isGeneratingAi ? 'none' : 'auto',
                    cursor: isGeneratingAi ? 'not-allowed' : 'pointer',
                  }}
                >
                  {isGeneratingAi ? (
                    <>
                      <i className="fas fa-spinner fa-spin" /> Generating Sequence...
                    </>
                  ) : hasGeneratedSequence ? (
                    <>
                      <i className="fas fa-sync-alt" /> Regenerate Sequence with AI
                    </>
                  ) : (
                    <>
                      <i className="fas fa-magic" /> Generate Sequence with AI
                    </>
                  )}
                </button>
                <button
                  type="button"
                  className="cmp-btn-secondary"
                  onClick={onAddJourneyStep}
                  disabled={isGeneratingAi}
                  style={{
                    pointerEvents: isGeneratingAi ? 'none' : 'auto',
                    cursor: isGeneratingAi ? 'not-allowed' : 'pointer',
                  }}
                >
                  <i className="fas fa-plus" /> Add First Step
                </button>
              </div>
            )}
          </div>
        ) : (
          <>
            {/* ── INNOVATIVE LIVE SEQUENCE BUBBLE MATRIX ── */}
            {(() => {
              const totalCohortLeads = effectiveLeads.length;
              const completedStepsCount = (formData?.journey || []).filter(
                (item, idx) => computeStepStatus(item, idx) === 'completed'
              ).length;

              const activeBubbleIdx = selectedBubbleIndex;
              const activeBubbleStep =
                activeBubbleIdx !== null && formData?.journey
                  ? formData.journey[activeBubbleIdx]
                  : null;

              let activeMeta = null;
              if (activeBubbleIdx !== null && activeBubbleStep) {
                const item = activeBubbleStep;
                const idx = activeBubbleIdx;
                const coverage = getStepChannelCoverage(item.channel, reachabilityStats);
                const currentStatus = computeStepStatus(item, idx);
                const deliveryMap = {
                  ...(item.leadOutreachStatus || {}),
                  ...(leadDeliveryMap[idx] || {}),
                };
                const stepLeads = effectiveLeads.filter(
                  (lead) =>
                    !hasLeadRepliedBeforeStep(lead, idx, formData?.journey, leadResponsesMap)
                );
                const totalLeads = stepLeads.length;
                const sentCount = stepLeads.filter((l) => isLeadSent(l, deliveryMap)).length;
                const deliveryPct = totalLeads > 0 ? Math.round((sentCount / totalLeads) * 100) : 0;
                const nominalD = item.nominalDate || computeDefaultNominalDate(item.day);
                const nominalT = item.nominalTime || timingConfig.sendWindowStart || '09:00';
                const breakdown = computeStepLeadBreakdown(
                  effectiveLeads,
                  item.channel || 'Email',
                  item.fallbackChannel || 'Auto',
                  formData.channels && formData.channels.length > 0
                    ? formData.channels
                    : ['Email', 'LinkedIn', 'WhatsApp', 'Phone Call'],
                  stepLeadOverrides[idx] || {}
                );

                let formattedDate = `Day ${item.day}`;
                if (nominalD) {
                  try {
                    const d = new Date(nominalD);
                    if (!isNaN(d.getTime())) {
                      const today = new Date();
                      const isToday =
                        d.getFullYear() === today.getFullYear() &&
                        d.getMonth() === today.getMonth() &&
                        d.getDate() === today.getDate();
                      const dStr = d.toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                      });
                      formattedDate = isToday ? `Today, ${dStr}` : dStr;
                    } else {
                      formattedDate = nominalD;
                    }
                  } catch (e) {
                    formattedDate = nominalD;
                  }
                }

                let formattedTime = '9:00 AM';
                if (nominalT) {
                  const parts = String(nominalT).split(':');
                  if (parts.length >= 2) {
                    const hours = parseInt(parts[0], 10);
                    const mins = parts[1];
                    const ampm = hours >= 12 ? 'PM' : 'AM';
                    const h12 = hours % 12 || 12;
                    formattedTime = `${h12}:${mins} ${ampm}`;
                  } else {
                    formattedTime = nominalT;
                  }
                }

                activeMeta = {
                  item,
                  idx,
                  stepItem: item,
                  stepIndex: idx,
                  step: item.step,
                  day: item.day,
                  name: item.name,
                  channel: item.channel,
                  condition: item.condition || 'Initial Touch',
                  rationale: item.channelRationale,
                  formattedDate,
                  formattedTime,
                  nominalDate: nominalD,
                  nominalTime: nominalT,
                  timezone: cohortTimezoneSummary?.badgeText || 'Local Time',
                  currentStatus,
                  totalLeads,
                  sentCount,
                  deliveryPct,
                  coverage,
                  breakdown,
                  isBlocked: isStepBlockedByCap(item, idx),
                };
              }

              return (
                <div className="cmp-live-sequence-canvas">
                  <div className="cmp-live-sequence-inner-wrap">
                    {/* Left/Center: Vertical Bubble Conduit Flow */}
                    <div className="cmp-bubble-flow-column">
                      {formData.journey.map((item, idx) => {
                        const prevItem = idx > 0 ? formData.journey[idx - 1] : null;
                        let waitDays = 0;
                        if (prevItem) {
                          const prevDate =
                            prevItem.nominalDate || computeDefaultNominalDate(prevItem.day);
                          const currDate = item.nominalDate || computeDefaultNominalDate(item.day);
                          if (prevDate && currDate) {
                            const diff = calculateDiffDays(prevDate, currDate);
                            waitDays =
                              !isNaN(diff) && diff >= 0
                                ? diff
                                : Math.max(
                                    0,
                                    (Number(item.day) || 1) - (Number(prevItem.day) || 1)
                                  );
                          } else {
                            waitDays = Math.max(
                              0,
                              (Number(item.day) || 1) - (Number(prevItem.day) || 1)
                            );
                          }
                        }
                        const waitLabel = waitDays === 1 ? 'wait 1 day' : `wait ${waitDays} days`;

                        const stepCurrentStatus = computeStepStatus(item, idx);
                        const stepDeliveryMap = {
                          ...(item.leadOutreachStatus || {}),
                          ...(leadDeliveryMap[idx] || {}),
                        };
                        const stepActiveLeads = effectiveLeads.filter(
                          (lead) =>
                            !hasLeadRepliedBeforeStep(
                              lead,
                              idx,
                              formData?.journey,
                              leadResponsesMap
                            )
                        );
                        const stepTotalLeads = stepActiveLeads.length;
                        const stepSentCount = stepActiveLeads.filter((l) =>
                          isLeadSent(l, stepDeliveryMap)
                        ).length;
                        const isBlocked = isStepBlockedByCap(item, idx);
                        const itemCoverage = getStepChannelCoverage(
                          item.channel,
                          reachabilityStats
                        );

                        const statusClass = isBlocked
                          ? 'cmp-bubble-blocked'
                          : stepCurrentStatus === 'completed' ||
                              (stepTotalLeads > 0 && stepSentCount === stepTotalLeads)
                            ? 'cmp-bubble-completed'
                            : stepCurrentStatus === 'pending' || stepSentCount > 0
                              ? 'cmp-bubble-pending'
                              : 'cmp-bubble-future';

                        const isHovered = hoveredBubbleIndex === idx;
                        const isSelected = selectedBubbleIndex === idx;
                        const displayTotal = stepTotalLeads || totalCohortLeads;

                        return (
                          <React.Fragment key={idx}>
                            {idx > 0 && (
                              <div className="cmp-bubble-conduit-connector">
                                <span className="cmp-journey-interval-pill">{waitLabel}</span>
                              </div>
                            )}
                            <div
                              id={`cmp-step-card-${idx}`}
                              className={`cmp-bubble-node-wrap ${statusClass} ${isHovered ? 'hovered' : ''} ${isSelected ? 'selected' : ''}`}
                              onMouseEnter={() => setHoveredBubbleIndex(idx)}
                              onMouseLeave={() => setHoveredBubbleIndex(null)}
                              onClick={() =>
                                setSelectedBubbleIndex((prev) => (prev === idx ? null : idx))
                              }
                              role="button"
                              tabIndex={0}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  setSelectedBubbleIndex((prev) => (prev === idx ? null : idx));
                                }
                              }}
                            >
                              <div className="cmp-bubble-halo">
                                <div className="cmp-bubble-circle">
                                  <i
                                    className={`${getChannelIcon(item.channel)} cmp-bubble-icon`}
                                  />
                                  <span className="cmp-bubble-step-num">
                                    {String(item.step).padStart(2, '0')}
                                  </span>
                                </div>
                                <span className="cmp-bubble-count-badge">
                                  {stepSentCount}/{displayTotal}
                                </span>
                              </div>

                              {/* Anchored Popover Speech Bubble on Hover */}
                              {isHovered && (
                                <div className="cmp-bubble-hover-popover" role="tooltip">
                                  <div className="cmp-popover-header">
                                    <span className="cmp-popover-step-tag">
                                      STEP {String(item.step).padStart(2, '0')} · DAY {item.day}
                                    </span>
                                    <span
                                      className={`cmp-popover-status-badge ${
                                        stepCurrentStatus === 'not yet'
                                          ? 'not_yet'
                                          : stepCurrentStatus
                                      }`}
                                    >
                                      {stepCurrentStatus}
                                    </span>
                                  </div>

                                  <strong className="cmp-popover-title">
                                    {item.name || `Touchpoint ${item.step}`}
                                  </strong>

                                  <div className="cmp-popover-meta">
                                    <span>
                                      <i className={getChannelIcon(item.channel)} /> {item.channel}
                                    </span>
                                    <span>·</span>
                                    <span>
                                      {item.nominalDate || computeDefaultNominalDate(item.day)} at{' '}
                                      {item.nominalTime || timingConfig.sendWindowStart || '09:00'}
                                    </span>
                                  </div>

                                  <div className="cmp-popover-footer">
                                    <span>
                                      <strong>
                                        {stepSentCount} / {displayTotal}
                                      </strong>{' '}
                                      sent
                                    </span>
                                    <span>·</span>
                                    <span>
                                      <strong>{itemCoverage.percentage}%</strong> reach
                                    </span>
                                  </div>
                                </div>
                              )}
                            </div>
                          </React.Fragment>
                        );
                      })}

                      {!isReadOnly && (
                        <div className="cmp-bubble-add-node-wrap">
                          <button
                            type="button"
                            className="cmp-bubble-add-btn"
                            disabled={isGeneratingAi || isLastStepAtOrPastEnd}
                            onClick={handleAddTouchpointAndFocus}
                            title={
                              isLastStepAtOrPastEnd
                                ? `Cannot add more touchpoints: campaign duration of ${campaignDurationDays} days is reached.`
                                : 'Add Touchpoint'
                            }
                          >
                            <i className="fas fa-plus" />
                            <span>Add Touchpoint</span>
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Right: Floating Live Sequence Inspector Panel */}
                    <div className="cmp-live-inspector-card">
                      {activeMeta ? (
                        <>
                          <div className="cmp-live-inspector-header">
                            <div className="cmp-inspector-header-left">
                              <span className="cmp-inspector-step-tag">
                                STEP {String(activeMeta.step).padStart(2, '0')}
                              </span>
                              <span className="cmp-inspector-day-tag">DAY {activeMeta.day}</span>
                              {isEditingStep && (
                                <span className="cmp-inspector-editing-badge">
                                  <i className="fas fa-pencil-alt" /> Editing Mode
                                </span>
                              )}
                            </div>
                            <div className="cmp-inspector-actions-wrap">
                              <button
                                type="button"
                                className="cmp-inspector-close-btn"
                                onClick={() => {
                                  setSelectedBubbleIndex(null);
                                  setHoveredBubbleIndex(null);
                                  setIsEditingStep(false);
                                }}
                                aria-label="Close Inspector"
                                title="Close"
                              >
                                &times;
                              </button>
                            </div>
                          </div>

                          {isEditingStep ? (
                            (() => {
                              const currentChannelLabel =
                                (
                                  SIMPLE_CHANNELS.find(
                                    (c) =>
                                      c.id.toLowerCase() ===
                                        (activeBubbleStep.channel || 'Email').toLowerCase() ||
                                      (activeBubbleStep.channel === 'Call' &&
                                        c.id === 'Phone Call') ||
                                      (activeBubbleStep.channel === 'Phone Call' && c.id === 'Call')
                                  ) || {}
                                ).label ||
                                activeBubbleStep.channel ||
                                'Email Outreach';

                              const activeConditionVal =
                                activeBubbleStep.condition ||
                                activeBubbleStep.rationale ||
                                (activeBubbleIdx === 0 ? 'Initial Touch' : 'If No Reply');

                              return (
                                <div
                                  className="cmp-inspector-edit-form"
                                  ref={editDropdownContainerRef}
                                >
                                  <div className="cmp-inspector-edit-group">
                                    <label className="cmp-inspector-edit-label">
                                      Touchpoint Title / Angle
                                    </label>
                                    <input
                                      type="text"
                                      className="cmp-inspector-input"
                                      value={activeBubbleStep.name || ''}
                                      placeholder={`e.g. Touchpoint ${activeBubbleStep.step}`}
                                      onChange={(e) =>
                                        onUpdateJourneyStep(activeBubbleIdx, 'name', e.target.value)
                                      }
                                    />
                                  </div>

                                  <div className="cmp-inspector-edit-row">
                                    <div className="cmp-inspector-edit-group flex-1">
                                      <label className="cmp-inspector-edit-label">Day Offset</label>
                                      <div className="cmp-day-input-wrap">
                                        <span>DAY</span>
                                        <input
                                          type="number"
                                          min="1"
                                          max={maxCampaignDay}
                                          className="cmp-field-input-sm cmp-day-number-input cmp-inspector-input"
                                          value={activeBubbleStep.day}
                                          onChange={(e) =>
                                            handleDayInputChange(
                                              activeBubbleIdx,
                                              parseInt(e.target.value, 10) || 1
                                            )
                                          }
                                        />
                                      </div>
                                    </div>

                                    <div className="cmp-inspector-edit-group flex-1">
                                      <label className="cmp-inspector-edit-label">
                                        Primary Channel
                                      </label>
                                      <div className="cmp-simple-select-wrap">
                                        <button
                                          type="button"
                                          className={`cmp-simple-select-trigger ${openEditDropdown === 'channel' ? 'open' : ''}`}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setOpenEditDropdown((prev) =>
                                              prev === 'channel' ? null : 'channel'
                                            );
                                          }}
                                          aria-label="Channel"
                                        >
                                          <span className="cmp-simple-select-value">
                                            {currentChannelLabel}
                                          </span>
                                          <i
                                            className={`fas fa-chevron-down cmp-simple-select-chevron ${
                                              openEditDropdown === 'channel' ? 'open' : ''
                                            }`}
                                          />
                                        </button>

                                        {openEditDropdown === 'channel' && (
                                          <div className="cmp-simple-select-menu">
                                            {SIMPLE_CHANNELS.map((ch) => {
                                              const isSelected =
                                                (
                                                  activeBubbleStep.channel || 'Email'
                                                ).toLowerCase() === ch.id.toLowerCase() ||
                                                (activeBubbleStep.channel === 'Call' &&
                                                  ch.id === 'Phone Call') ||
                                                (activeBubbleStep.channel === 'Phone Call' &&
                                                  ch.id === 'Call');
                                              return (
                                                <div
                                                  key={ch.id}
                                                  className={`cmp-simple-select-option ${isSelected ? 'selected' : ''}`}
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    onUpdateJourneyStep(
                                                      activeBubbleIdx,
                                                      'channel',
                                                      ch.id
                                                    );
                                                    setOpenEditDropdown(null);
                                                  }}
                                                >
                                                  {ch.label}
                                                </div>
                                              );
                                            })}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>

                                  <div className="cmp-inspector-edit-row">
                                    <div className="cmp-inspector-edit-group flex-1">
                                      <label className="cmp-inspector-edit-label">
                                        Nominal Date
                                      </label>
                                      <input
                                        type="date"
                                        className="cmp-field-input-date cmp-inspector-input"
                                        value={
                                          activeBubbleStep.nominalDate ||
                                          computeDefaultNominalDate(activeBubbleStep.day)
                                        }
                                        min={campaignStartDate}
                                        max={campaignEndDate}
                                        onChange={(e) =>
                                          handleStepDateChange(activeBubbleIdx, e.target.value)
                                        }
                                      />
                                    </div>

                                    <div className="cmp-inspector-edit-group flex-1">
                                      <label className="cmp-inspector-edit-label">
                                        Nominal Time
                                      </label>
                                      <input
                                        type="time"
                                        className="cmp-field-input-time cmp-inspector-input"
                                        value={
                                          activeBubbleStep.nominalTime ||
                                          timingConfig.sendWindowStart ||
                                          '09:00'
                                        }
                                        min={timingConfig.sendWindowStart || '09:00'}
                                        max={timingConfig.sendWindowEnd || '18:00'}
                                        onChange={(e) =>
                                          handleStepTimeChange(activeBubbleIdx, e.target.value)
                                        }
                                      />
                                    </div>
                                  </div>

                                  <div className="cmp-inspector-edit-group">
                                    <label className="cmp-inspector-edit-label">
                                      Condition / Trigger
                                    </label>
                                    <div className="cmp-simple-select-wrap">
                                      <button
                                        type="button"
                                        className={`cmp-simple-select-trigger ${openEditDropdown === 'condition' ? 'open' : ''}`}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setOpenEditDropdown((prev) =>
                                            prev === 'condition' ? null : 'condition'
                                          );
                                        }}
                                        aria-label="Condition / Trigger"
                                      >
                                        <span className="cmp-simple-select-value">
                                          {activeConditionVal}
                                        </span>
                                        <i
                                          className={`fas fa-chevron-down cmp-simple-select-chevron ${
                                            openEditDropdown === 'condition' ? 'open' : ''
                                          }`}
                                        />
                                      </button>

                                      {openEditDropdown === 'condition' && (
                                        <div className="cmp-simple-select-menu">
                                          {SIMPLE_CONDITIONS.map((cond) => {
                                            const isSelected = activeConditionVal === cond;
                                            return (
                                              <div
                                                key={cond}
                                                className={`cmp-simple-select-option ${isSelected ? 'selected' : ''}`}
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  onUpdateJourneyStep(
                                                    activeBubbleIdx,
                                                    'condition',
                                                    cond
                                                  );
                                                  setOpenEditDropdown(null);
                                                }}
                                              >
                                                {cond}
                                              </div>
                                            );
                                          })}
                                        </div>
                                      )}
                                    </div>
                                  </div>

                                  <div className="cmp-inspector-edit-group">
                                    <label className="cmp-inspector-edit-label">
                                      Execution Status
                                    </label>
                                    <div className="cmp-simple-select-wrap">
                                      <button
                                        type="button"
                                        className={`cmp-simple-select-trigger ${openEditDropdown === 'edit_status' ? 'open' : ''}`}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setOpenEditDropdown((prev) =>
                                            prev === 'edit_status' ? null : 'edit_status'
                                          );
                                        }}
                                        aria-label="Execution Status"
                                      >
                                        <span
                                          className="cmp-simple-select-value"
                                          style={{ textTransform: 'capitalize' }}
                                        >
                                          {activeMeta.currentStatus}
                                        </span>
                                        <i
                                          className={`fas fa-chevron-down cmp-simple-select-chevron ${
                                            openEditDropdown === 'edit_status' ? 'open' : ''
                                          }`}
                                        />
                                      </button>

                                      {openEditDropdown === 'edit_status' && (
                                        <div className="cmp-simple-select-menu">
                                          {['not yet', 'pending', 'completed'].map((st) => (
                                            <div
                                              key={st}
                                              className={`cmp-simple-select-option ${activeMeta.currentStatus === st ? 'selected' : ''}`}
                                              style={{ textTransform: 'capitalize' }}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleManualStatusChange(activeBubbleIdx, st);
                                                setOpenEditDropdown(null);
                                              }}
                                            >
                                              {st}
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  </div>

                                  <div className="cmp-inspector-edit-actions">
                                    <button
                                      type="button"
                                      className="cmp-inspector-cancel-btn"
                                      onClick={() => {
                                        setIsEditingStep(false);
                                        setOpenEditDropdown(null);
                                      }}
                                      title="Cancel editing without saving changes"
                                    >
                                      <i className="fas fa-times" /> Cancel
                                    </button>
                                    <button
                                      type="button"
                                      className="cmp-inspector-save-btn"
                                      onClick={() => {
                                        setIsEditingStep(false);
                                        setOpenEditDropdown(null);
                                      }}
                                      title="Save and finish editing"
                                    >
                                      <i className="fas fa-check" /> Save & Done
                                    </button>
                                  </div>
                                </div>
                              );
                            })()
                          ) : (
                            <>
                              <div className="cmp-inspector-title-row">
                                <h4 className="cmp-inspector-title">
                                  {activeMeta.name || `Touchpoint ${activeMeta.step}`}
                                </h4>
                              </div>

                              <div className="cmp-inspector-rows">
                                <div className="cmp-inspector-row">
                                  <span className="label">Status</span>
                                  <span
                                    className="val"
                                    style={{
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: '6px',
                                    }}
                                  >
                                    <div
                                      className="cmp-simple-select-wrap"
                                      style={{ display: 'inline-block', width: 'auto' }}
                                    >
                                      <button
                                        type="button"
                                        className={`cmp-simple-select-trigger ${openEditDropdown === 'read_status' ? 'open' : ''}`}
                                        style={{
                                          minHeight: '26px',
                                          padding: '2px 8px',
                                          fontSize: '12px',
                                          fontWeight: 600,
                                          borderRadius: '6px',
                                          background: '#f8fafc',
                                        }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          if (isReadOnly) return;
                                          setOpenEditDropdown((prev) =>
                                            prev === 'read_status' ? null : 'read_status'
                                          );
                                        }}
                                        title="Click to update touchpoint execution status"
                                      >
                                        <span
                                          className="cmp-simple-select-value"
                                          style={{ textTransform: 'capitalize' }}
                                        >
                                          {activeMeta.currentStatus}
                                        </span>
                                        {!isReadOnly && (
                                          <i
                                            className={`fas fa-chevron-down cmp-simple-select-chevron ${
                                              openEditDropdown === 'read_status' ? 'open' : ''
                                            }`}
                                          />
                                        )}
                                      </button>

                                      {!isReadOnly && openEditDropdown === 'read_status' && (
                                        <div
                                          className="cmp-simple-select-menu"
                                          style={{
                                            right: 0,
                                            left: 'auto',
                                            minWidth: '120px',
                                          }}
                                        >
                                          {['not yet', 'pending', 'completed'].map((st) => (
                                            <div
                                              key={st}
                                              className={`cmp-simple-select-option ${activeMeta.currentStatus === st ? 'selected' : ''}`}
                                              style={{ textTransform: 'capitalize' }}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleManualStatusChange(activeBubbleIdx, st);
                                                setOpenEditDropdown(null);
                                              }}
                                            >
                                              {st}
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                    <span style={{ color: '#64748b', fontSize: '11.5px' }}>
                                      ({activeMeta.sentCount}/{activeMeta.totalLeads})
                                    </span>
                                  </span>
                                </div>
                                <div className="cmp-inspector-row">
                                  <span className="label">Channel</span>
                                  <span
                                    className="val"
                                    style={{
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: '6px',
                                    }}
                                  >
                                    <i className={getChannelIcon(activeMeta.channel)} />{' '}
                                    {activeMeta.channel}
                                  </span>
                                </div>
                                <div className="cmp-inspector-row">
                                  <span className="label">Date</span>
                                  <span className="val">{activeMeta.formattedDate}</span>
                                </div>
                                <div className="cmp-inspector-row">
                                  <span className="label">Time</span>
                                  <span className="val">
                                    {activeMeta.formattedTime} ·{' '}
                                    <span className="cmp-tz-badge-pill">{activeMeta.timezone}</span>
                                  </span>
                                </div>
                                <div className="cmp-inspector-row">
                                  <span className="label">Reachability</span>
                                  <span
                                    className="val"
                                    style={{
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      flexWrap: 'wrap',
                                      gap: '4px',
                                      justifyContent: 'flex-end',
                                    }}
                                  >
                                    <span>
                                      {activeMeta.coverage.count}/{activeMeta.coverage.total} (
                                      {activeMeta.coverage.percentage}%)
                                    </span>
                                    {activeMeta.coverage.hasCoverageGap ? (
                                      <span className="cmp-inspector-gap-tag">
                                        {activeMeta.coverage.missing} alt needed
                                      </span>
                                    ) : (
                                      <span className="cmp-inspector-full-tag">Full Reach</span>
                                    )}
                                    {activeMeta.breakdown?.counts?.fallbackCount > 0 && (
                                      <span className="cmp-inspector-fallback-tag">
                                        +{activeMeta.breakdown.counts.fallbackCount} fallback
                                      </span>
                                    )}
                                  </span>
                                </div>
                                <div className="cmp-inspector-row">
                                  <span className="label">Leads sent</span>
                                  <span className="val">
                                    {activeMeta.sentCount} / {activeMeta.totalLeads}
                                  </span>
                                </div>
                              </div>

                              <div className="cmp-inspector-condition-box">
                                {activeMeta.condition || activeMeta.rationale || 'Initial Touch'}
                              </div>

                              <div className="cmp-inspector-footer-row">
                                {!isReadOnly && (
                                  <div
                                    style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                                  >
                                    <button
                                      type="button"
                                      className="cmp-inspector-secondary-edit-btn"
                                      onClick={() => setIsEditingStep(true)}
                                      title="Edit touchpoint properties"
                                    >
                                      <i className="fas fa-sliders-h" /> Edit Step
                                    </button>
                                    {formData.journey && formData.journey.length > 1 && (
                                      <button
                                        type="button"
                                        className="cmp-inspector-delete-icon-btn"
                                        onClick={() => {
                                          onRemoveJourneyStep(activeBubbleIdx);
                                          setIsEditingStep(false);
                                          setOpenEditDropdown(null);
                                          setSelectedBubbleIndex((prev) => Math.max(0, prev - 1));
                                        }}
                                        title="Delete this touchpoint step"
                                        aria-label="Delete Touchpoint"
                                      >
                                        <i className="fas fa-trash-alt" />
                                      </button>
                                    )}
                                  </div>
                                )}

                                {activeMeta.currentStatus === 'completed' ||
                                (activeMeta.totalLeads > 0 &&
                                  activeMeta.sentCount === activeMeta.totalLeads) ? (
                                  <button
                                    type="button"
                                    className="cmp-inspector-sent-pill"
                                    style={{ margin: 0 }}
                                    onClick={() => {
                                      if (onLaunchOutreach && !isCreateMode) {
                                        onLaunchOutreach({
                                          ...activeBubbleStep,
                                          stepIndex: activeBubbleIdx,
                                          eligibleLeads: effectiveLeads.filter(
                                            (lead) =>
                                              !hasLeadRepliedBeforeStep(
                                                lead,
                                                activeBubbleIdx,
                                                formData?.journey,
                                                leadResponsesMap
                                              )
                                          ),
                                          stepLeadOverrides:
                                            stepLeadOverrides[activeBubbleIdx] ||
                                            activeBubbleStep.stepLeadOverrides ||
                                            {},
                                        });
                                      }
                                    }}
                                  >
                                    <i className="fas fa-paper-plane" /> Sent
                                  </button>
                                ) : (
                                  <button
                                    type="button"
                                    className={`cmp-inspector-launch-pill ${
                                      isStepArrivedAttention(activeBubbleStep, activeBubbleIdx)
                                        ? 'cmp-inspector-launch-pill-attention'
                                        : ''
                                    }`}
                                    disabled={isCreateMode || activeMeta.isBlocked}
                                    style={{
                                      margin: 0,
                                      ...(isCreateMode
                                        ? { opacity: 0.6, cursor: 'not-allowed' }
                                        : {}),
                                    }}
                                    title={
                                      isCreateMode
                                        ? 'Outreach launching is available after the campaign is created and approved/activated.'
                                        : `Launch ${activeMeta.channel} Outreach`
                                    }
                                    onClick={() => {
                                      if (isCreateMode || activeMeta.isBlocked) return;
                                      setClickedAttentionSteps((prev) => {
                                        const next = new Set(prev);
                                        next.add(activeBubbleIdx);
                                        return next;
                                      });
                                      if (onLaunchOutreach) {
                                        onLaunchOutreach({
                                          ...activeBubbleStep,
                                          stepIndex: activeBubbleIdx,
                                          eligibleLeads: effectiveLeads.filter(
                                            (lead) =>
                                              !hasLeadRepliedBeforeStep(
                                                lead,
                                                activeBubbleIdx,
                                                formData?.journey,
                                                leadResponsesMap
                                              )
                                          ),
                                          stepLeadOverrides:
                                            stepLeadOverrides[activeBubbleIdx] ||
                                            activeBubbleStep.stepLeadOverrides ||
                                            {},
                                        });
                                      }
                                    }}
                                  >
                                    <i className="fas fa-paper-plane" /> Launch {activeMeta.channel}
                                  </button>
                                )}
                              </div>
                            </>
                          )}
                        </>
                      ) : (
                        <>
                          <div className="cmp-live-inspector-header">
                            <span className="cmp-live-seq-badge">LIVE SEQUENCE</span>
                            <span className="cmp-live-pulse-dot" />
                          </div>

                          <h4 className="cmp-live-inspector-title">Hover a bubble</h4>

                          <p className="cmp-live-inspector-desc">
                            Each bubble is a moment in the journey. Hover for a preview, click to
                            open the full step.
                          </p>

                          <div className="cmp-live-inspector-stats">
                            <div className="cmp-live-inspector-stat-row">
                              <span className="label">Audience</span>
                              <span className="val">{totalCohortLeads} leads</span>
                            </div>
                            <div className="cmp-live-inspector-stat-row">
                              <span className="label">Steps complete</span>
                              <span className="val">
                                {completedStepsCount} / {formData.journey.length}
                              </span>
                            </div>
                          </div>

                          {!isReadOnly && (
                            <button
                              type="button"
                              className="cmp-btn-add-touchpoint-live"
                              disabled={isGeneratingAi || isLastStepAtOrPastEnd}
                              onClick={handleAddTouchpointAndFocus}
                              title={
                                isLastStepAtOrPastEnd
                                  ? `Cannot add more touchpoints: campaign duration of ${campaignDurationDays} days is reached.`
                                  : 'Add a new touchpoint step to this sequence'
                              }
                            >
                              <i className="fas fa-plus" /> Add New Touchpoint
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Dedicated Tabbed Drawer Container: Reachability Breakdown & Delivery Records */}
            {formData.journey &&
              formData.journey.length > 0 &&
              selectedBubbleIndex !== null &&
              (() => {
                const activeDetailIdx = selectedBubbleIndex;
                const activeDetailStep = formData.journey[activeDetailIdx];
                if (!activeDetailStep) return null;

                const currentStatus = computeStepStatus(activeDetailStep, activeDetailIdx);
                const isBlocked = isStepBlockedByCap(activeDetailStep, activeDetailIdx);
                const stepActiveLeads = effectiveLeads.filter(
                  (lead) =>
                    !hasLeadRepliedBeforeStep(
                      lead,
                      activeDetailIdx,
                      formData?.journey,
                      leadResponsesMap
                    )
                );
                const breakdown = computeStepLeadBreakdown(
                  stepActiveLeads,
                  activeDetailStep.channel || 'Email',
                  activeDetailStep.fallbackChannel || 'Auto',
                  formData.channels && formData.channels.length > 0
                    ? formData.channels
                    : ['Email', 'LinkedIn', 'WhatsApp', 'Phone Call'],
                  stepLeadOverrides[activeDetailIdx] || {}
                );

                const stepDeliveryMap = {
                  ...(activeDetailStep.leadOutreachStatus || {}),
                  ...(leadDeliveryMap[activeDetailIdx] || {}),
                };
                const sentLeads = stepActiveLeads.filter((l) => isLeadSent(l, stepDeliveryMap));
                const notSentLeads = stepActiveLeads.filter((l) => !isLeadSent(l, stepDeliveryMap));
                const displayedLeads =
                  activeDeliveryTab === 'all'
                    ? stepActiveLeads
                    : activeDeliveryTab === 'sent'
                      ? sentLeads
                      : notSentLeads;

                const stepResponsesMap = {
                  ...(activeDetailStep.leadResponses || {}),
                  ...(leadResponsesMap[activeDetailIdx] || {}),
                };
                const positiveLeads = stepActiveLeads.filter((l) => {
                  const rec = getLeadResponseRecord(l, stepResponsesMap);
                  const rt = rec?.responseType || l?.leadResponse;
                  return (
                    rt === 'positive' ||
                    rt === 'interested' ||
                    l?.stage === 'Qualified' ||
                    l?.stage === 'Won'
                  );
                });
                const negativeLeads = stepActiveLeads.filter((l) => {
                  const rec = getLeadResponseRecord(l, stepResponsesMap);
                  const rt = rec?.responseType || l?.leadResponse;
                  return (
                    rt === 'negative' ||
                    rt === 'not_interested' ||
                    (l?.stage === 'Lost' &&
                      (l?.lostReason === 'Not interested' || l?.lostReason === 'not_interested'))
                  );
                });
                const noReplyLeads = stepActiveLeads.filter((l) => {
                  const rec = getLeadResponseRecord(l, stepResponsesMap);
                  const rt = rec?.responseType || l?.leadResponse;
                  const isPositive =
                    rt === 'positive' ||
                    rt === 'interested' ||
                    l?.stage === 'Qualified' ||
                    l?.stage === 'Won';
                  const isNegative =
                    rt === 'negative' ||
                    rt === 'not_interested' ||
                    (l?.stage === 'Lost' &&
                      (l?.lostReason === 'Not interested' || l?.lostReason === 'not_interested'));
                  return !isPositive && !isNegative;
                });

                return (
                  <div
                    className="cmp-below-grid-detail-container"
                    id="cmp-below-grid-detail-container"
                  >
                    <div className="cmp-below-grid-tab-header">
                      <div className="cmp-below-grid-step-indicator">
                        <span className="cmp-below-step-pill">
                          STEP {String(activeDetailStep.step).padStart(2, '0')}
                        </span>
                        <span className="cmp-below-day-pill">DAY {activeDetailStep.day}</span>
                        <strong className="cmp-below-step-name">
                          {activeDetailStep.name || `Touchpoint ${activeDetailStep.step}`}
                        </strong>
                        <span className="cmp-below-channel-badge">
                          <i className={getChannelIcon(activeDetailStep.channel)} />{' '}
                          {activeDetailStep.channel}
                        </span>
                        <span
                          className={`cmp-below-status-badge ${
                            currentStatus === 'not yet' ? 'not_yet' : currentStatus
                          }`}
                        >
                          {currentStatus}
                        </span>
                        {!isReadOnly && (
                          <button
                            type="button"
                            className={`cmp-below-edit-btn ${isEditingStep ? 'active' : ''}`}
                            onClick={() => setIsEditingStep((prev) => !prev)}
                            title={isEditingStep ? 'Finish editing step' : 'Edit step properties'}
                          >
                            <i className={isEditingStep ? 'fas fa-check' : 'fas fa-edit'} />
                            <span>{isEditingStep ? 'Done Editing' : 'Edit Step'}</span>
                          </button>
                        )}
                      </div>

                      <div className="cmp-below-grid-tabs">
                        <button
                          type="button"
                          className={`cmp-below-grid-tab-btn ${activeBelowGridTab === 'reachability' ? 'active' : ''}`}
                          onClick={() => setActiveBelowGridTab('reachability')}
                        >
                          <i className="fas fa-network-wired" /> Lead Routing & Channel Breakdown
                          <span className="cmp-below-tab-count">
                            {breakdown.counts.coveredCount}/{breakdown.counts.total}
                          </span>
                        </button>
                        <button
                          type="button"
                          className={`cmp-below-grid-tab-btn ${activeBelowGridTab === 'delivery' ? 'active' : ''}`}
                          onClick={() => setActiveBelowGridTab('delivery')}
                        >
                          <i className="fas fa-paper-plane" /> Message Delivery & Response Record
                          <span className="cmp-below-tab-count">
                            {sentLeads.length}/{stepActiveLeads.length}
                          </span>
                        </button>
                      </div>
                    </div>

                    <div className="cmp-below-grid-tab-body">
                      {/* TAB 1: Lead Routing & Reachability Breakdown */}
                      {activeBelowGridTab === 'reachability' && (
                        <div className="cmp-step-lead-breakdown-drawer cmp-below-grid-drawer">
                          <div className="cmp-drawer-top-bar">
                            <div className="cmp-drawer-title-col">
                              <p className="cmp-drawer-subheading">
                                <strong>
                                  {breakdown.counts.coveredCount} of {breakdown.counts.total} leads
                                  contactable ({breakdown.counts.coveragePct}%)
                                </strong>{' '}
                                • {breakdown.counts.primaryCount} via {activeDetailStep.channel}{' '}
                                (Primary), {breakdown.counts.fallbackCount} via Fallback (
                                {activeDetailStep.fallbackChannel || 'Auto'})
                                {breakdown.counts.unreachableCount > 0
                                  ? `, ${breakdown.counts.unreachableCount} cannot be contacted`
                                  : ''}
                                .
                              </p>
                            </div>
                          </div>

                          {(activeDetailStep.condition || activeDetailStep.rationale) && (
                            <div className="cmp-drawer-rationale-box">
                              <i className="fas fa-robot" />
                              <div>
                                <strong>7-Factor Recommendation:</strong>{' '}
                                {activeDetailStep.rationale || activeDetailStep.condition}
                              </div>
                            </div>
                          )}

                          <div className="cmp-drawer-filter-nav">
                            <button
                              type="button"
                              className={`cmp-drawer-nav-btn ${activeBreakdownTab === 'all' ? 'active' : ''}`}
                              onClick={() => setActiveBreakdownTab('all')}
                            >
                              <i className="fas fa-users" /> All ({breakdown.counts.total})
                            </button>
                            <button
                              type="button"
                              className={`cmp-drawer-nav-btn primary ${activeBreakdownTab === 'primary' ? 'active' : ''}`}
                              onClick={() => setActiveBreakdownTab('primary')}
                            >
                              <i className="fas fa-check-circle" /> Primary (
                              {breakdown.counts.primaryCount})
                            </button>
                            <button
                              type="button"
                              className={`cmp-drawer-nav-btn fallback ${activeBreakdownTab === 'fallback' ? 'active' : ''}`}
                              onClick={() => setActiveBreakdownTab('fallback')}
                            >
                              <i className="fas fa-exchange-alt" /> Fallback (
                              {breakdown.counts.fallbackCount})
                            </button>
                            <button
                              type="button"
                              className={`cmp-drawer-nav-btn unreachable ${activeBreakdownTab === 'unreachable' ? 'active' : ''}`}
                              onClick={() => setActiveBreakdownTab('unreachable')}
                            >
                              <i className="fas fa-ban" /> Can't Contact (
                              {breakdown.counts.unreachableCount})
                            </button>
                          </div>

                          <div className="cmp-drawer-table-wrap">
                            <table className="cmp-drawer-leads-table">
                              <thead>
                                <tr>
                                  <th>Lead & Company</th>
                                  <th>Primary ({activeDetailStep.channel})</th>
                                  <th>Assigned Route</th>
                                  <th>Route Override</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(activeBreakdownTab === 'all'
                                  ? breakdown.allLeads
                                  : activeBreakdownTab === 'primary'
                                    ? breakdown.primaryLeads
                                    : activeBreakdownTab === 'fallback'
                                      ? breakdown.fallbackLeads
                                      : breakdown.unreachableLeads
                                ).map((leadRow) => {
                                  const isPrimary = leadRow.routeType === 'primary';
                                  const isFallback = leadRow.routeType === 'fallback';
                                  const isUnreachable = leadRow.routeType === 'unreachable';
                                  return (
                                    <tr
                                      key={leadRow.leadId}
                                      className={`cmp-lead-row ${leadRow.routeType}`}
                                    >
                                      <td>
                                        <div className="cmp-lead-name-cell">
                                          <strong title={leadRow.name}>{leadRow.name}</strong>
                                          <span title={`${leadRow.title} • ${leadRow.company}`}>
                                            {leadRow.title} • {leadRow.company}
                                          </span>
                                        </div>
                                      </td>
                                      <td>
                                        {leadRow.hasPrimary ? (
                                          <span className="cmp-status-badge has-primary">
                                            <i className="fas fa-check text-success" /> Available
                                          </span>
                                        ) : (
                                          <span className="cmp-status-badge missing-primary">
                                            <i className="fas fa-times text-danger" /> Missing{' '}
                                            {activeDetailStep.channel}
                                          </span>
                                        )}
                                      </td>
                                      <td>
                                        {isPrimary && (
                                          <span className="cmp-route-chip primary">
                                            <i
                                              className={getChannelIcon(activeDetailStep.channel)}
                                            />{' '}
                                            {activeDetailStep.channel} (Primary)
                                          </span>
                                        )}
                                        {isFallback && (
                                          <span className="cmp-route-chip fallback">
                                            <i
                                              className={getChannelIcon(leadRow.resolvedChannel)}
                                            />{' '}
                                            {leadRow.resolvedChannel} (Fallback)
                                          </span>
                                        )}
                                        {isUnreachable && (
                                          <span className="cmp-route-chip unreachable">
                                            <i className="fas fa-ban" /> Unreachable
                                          </span>
                                        )}
                                      </td>
                                      <td>
                                        <div className="cmp-route-override-cell">
                                          <select
                                            value={leadRow.currentOverride || 'auto'}
                                            onChange={(e) =>
                                              handleOverrideLeadChannel(
                                                activeDetailIdx,
                                                leadRow.leadId,
                                                e.target.value
                                              )
                                            }
                                            className="cmp-route-override-select"
                                            disabled={isReadOnly}
                                          >
                                            <option value="auto">
                                              Auto (
                                              {leadRow.routeType === 'unreachable'
                                                ? 'Unreachable'
                                                : leadRow.resolvedChannel || 'None'}
                                              )
                                            </option>
                                            {leadRow.hasPrimary && (
                                              <option value={activeDetailStep.channel}>
                                                {activeDetailStep.channel} (Primary)
                                              </option>
                                            )}
                                            {leadRow.email &&
                                              activeDetailStep.channel !== 'Email' && (
                                                <option value="Email">Email</option>
                                              )}
                                            {leadRow.phone &&
                                              activeDetailStep.channel !== 'WhatsApp' && (
                                                <option value="WhatsApp">WhatsApp</option>
                                              )}
                                            {leadRow.phone &&
                                              activeDetailStep.channel !== 'Phone' && (
                                                <option value="Phone">Phone</option>
                                              )}
                                            {leadRow.linkedin &&
                                              activeDetailStep.channel !== 'LinkedIn' && (
                                                <option value="LinkedIn">LinkedIn</option>
                                              )}
                                            <option value="skip">Skip This Step</option>
                                          </select>
                                        </div>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      {/* TAB 2: Message Delivery & Response Record */}
                      {activeBelowGridTab === 'delivery' && (
                        <div className="cmp-step-lead-breakdown-drawer cmp-below-grid-drawer">
                          <div className="cmp-drawer-top-bar">
                            <div className="cmp-drawer-title-col">
                              <div className="cmp-drawer-status-override-row">
                                <span className="cmp-status-label">Execution Status:</span>
                                <div className="cmp-custom-status-dropdown-wrap">
                                  <button
                                    type="button"
                                    className={`cmp-custom-status-trigger ${
                                      currentStatus === 'not yet' ? 'not_yet' : currentStatus
                                    }`}
                                    onClick={() =>
                                      !isReadOnly &&
                                      setStatusDropdownOpenStepIndex((prev) =>
                                        prev === activeDetailIdx ? null : activeDetailIdx
                                      )
                                    }
                                    disabled={isReadOnly}
                                  >
                                    <span className="cmp-status-trigger-dot" />
                                    <span className="cmp-status-trigger-text">
                                      {currentStatus === 'completed'
                                        ? 'Completed'
                                        : currentStatus === 'pending'
                                          ? 'Pending'
                                          : currentStatus === 'in_progress'
                                            ? 'In Progress'
                                            : currentStatus === 'blocked'
                                              ? 'Blocked'
                                              : 'Not Yet'}
                                    </span>
                                    {!isReadOnly && (
                                      <i className="fas fa-chevron-down cmp-status-trigger-chevron" />
                                    )}
                                  </button>

                                  {statusDropdownOpenStepIndex === activeDetailIdx && (
                                    <div className="cmp-custom-status-dropdown-menu">
                                      <div
                                        className={`cmp-custom-status-option ${
                                          currentStatus === 'completed' ? 'selected' : ''
                                        }`}
                                        onClick={() => {
                                          handleManualStatusChange(activeDetailIdx, 'completed');
                                          setStatusDropdownOpenStepIndex(null);
                                        }}
                                      >
                                        <div className="cmp-status-option-content">
                                          <strong className="cmp-status-option-title">
                                            Completed
                                          </strong>
                                          <span className="cmp-status-option-desc">
                                            Mark touchpoint as fully executed
                                          </span>
                                        </div>
                                        {currentStatus === 'completed' && (
                                          <i className="fas fa-check cmp-status-check-icon" />
                                        )}
                                      </div>

                                      <div
                                        className={`cmp-custom-status-option ${
                                          currentStatus === 'pending' ||
                                          currentStatus === 'in_progress'
                                            ? 'selected'
                                            : ''
                                        }`}
                                        onClick={() => {
                                          handleManualStatusChange(activeDetailIdx, 'pending');
                                          setStatusDropdownOpenStepIndex(null);
                                        }}
                                      >
                                        <div className="cmp-status-option-content">
                                          <strong className="cmp-status-option-title">
                                            Pending / In Progress
                                          </strong>
                                          <span className="cmp-status-option-desc">
                                            Currently dispatching outreach
                                          </span>
                                        </div>
                                        {(currentStatus === 'pending' ||
                                          currentStatus === 'in_progress') && (
                                          <i className="fas fa-check cmp-status-check-icon" />
                                        )}
                                      </div>

                                      <div
                                        className={`cmp-custom-status-option ${
                                          currentStatus === 'not yet' ? 'selected' : ''
                                        }`}
                                        onClick={() => {
                                          handleManualStatusChange(activeDetailIdx, 'not yet');
                                          setStatusDropdownOpenStepIndex(null);
                                        }}
                                      >
                                        <div className="cmp-status-option-content">
                                          <strong className="cmp-status-option-title">
                                            Not Yet
                                          </strong>
                                          <span className="cmp-status-option-desc">
                                            Scheduled in future
                                          </span>
                                        </div>
                                        {currentStatus === 'not yet' && (
                                          <i className="fas fa-check cmp-status-check-icon" />
                                        )}
                                      </div>

                                      {isBlocked && (
                                        <div
                                          className={`cmp-custom-status-option ${
                                            currentStatus === 'blocked' ? 'selected' : ''
                                          }`}
                                          onClick={() => {
                                            handleManualStatusChange(activeDetailIdx, 'blocked');
                                            setStatusDropdownOpenStepIndex(null);
                                          }}
                                        >
                                          <div className="cmp-status-option-content">
                                            <strong className="cmp-status-option-title">
                                              Blocked
                                            </strong>
                                            <span className="cmp-status-option-desc">
                                              Blocked by Guardrail
                                            </span>
                                          </div>
                                          {currentStatus === 'blocked' && (
                                            <i className="fas fa-check cmp-status-check-icon" />
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                              {activeDetailStep.isManualStatusOverride && (
                                <span className="cmp-manual-status-badge">
                                  <i className="fas fa-user-edit" /> Manually Overridden
                                </span>
                              )}
                            </div>
                          </div>

                          <div className="cmp-delivery-kpis">
                            <button
                              type="button"
                              className={`cmp-drawer-nav-btn ${activeDeliveryTab === 'all' ? 'active' : ''}`}
                              onClick={() => setActiveDeliveryTab('all')}
                            >
                              <i className="fas fa-users" /> All ({stepActiveLeads.length})
                            </button>
                            <button
                              type="button"
                              className={`cmp-drawer-nav-btn primary ${activeDeliveryTab === 'sent' ? 'active' : ''}`}
                              onClick={() => setActiveDeliveryTab('sent')}
                            >
                              <i className="fas fa-paper-plane" /> Sent ({sentLeads.length})
                            </button>
                            <button
                              type="button"
                              className={`cmp-drawer-nav-btn unreachable ${activeDeliveryTab === 'not_sent' ? 'active' : ''}`}
                              onClick={() => setActiveDeliveryTab('not_sent')}
                            >
                              <i className="fas fa-hourglass-half" /> Not Sent (
                              {notSentLeads.length})
                            </button>

                            {!isReadOnly && (
                              <div className="cmp-delivery-bulk-actions">
                                <button
                                  type="button"
                                  className="cmp-btn-bulk-action"
                                  onClick={() => handleMarkAllLeads(activeDetailIdx, true)}
                                  title="Mark all leads as sent"
                                >
                                  <i className="fas fa-check-double text-success" /> Mark All Sent
                                </button>
                                <button
                                  type="button"
                                  className="cmp-btn-bulk-action"
                                  onClick={() => handleMarkAllLeads(activeDetailIdx, false)}
                                  title="Reset all leads to not sent"
                                >
                                  <i className="fas fa-undo text-secondary" /> Mark All Unsent
                                </button>
                              </div>
                            )}
                          </div>

                          <div className="cmp-drawer-table-wrap">
                            <table className="cmp-drawer-leads-table">
                              <thead>
                                <tr>
                                  <th>Lead & Company</th>
                                  <th>Outreach Channel</th>
                                  <th>Delivery Status</th>
                                  <th>Sent Dispatch Time</th>
                                  {!isReadOnly && <th style={{ textAlign: 'right' }}>Action</th>}
                                </tr>
                              </thead>
                              <tbody>
                                {displayedLeads.length === 0 ? (
                                  <tr>
                                    <td
                                      colSpan={isReadOnly ? 4 : 5}
                                      style={{
                                        textAlign: 'center',
                                        padding: '16px',
                                        color: '#64748b',
                                      }}
                                    >
                                      No leads found for this delivery filter.
                                    </td>
                                  </tr>
                                ) : (
                                  displayedLeads.map((lead) => {
                                    const leadKey = String(
                                      lead.id || lead._id || lead.email || lead.name
                                    );
                                    const delivery =
                                      getLeadDeliveryRecord(lead, stepDeliveryMap) || {};
                                    const isSent = isLeadSent(lead, stepDeliveryMap);
                                    const leadRouting = resolveLeadRoutingRecord(lead, breakdown);
                                    const channelInfo = resolveLeadChannelInfo(
                                      lead,
                                      activeDetailStep,
                                      leadRouting
                                    );
                                    return (
                                      <tr key={leadKey}>
                                        <td>
                                          <div className="cmp-lead-name-cell">
                                            <div
                                              style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px',
                                              }}
                                            >
                                              <strong title={lead.name}>
                                                {lead.name || 'Unnamed Lead'}
                                              </strong>
                                              <span
                                                className={`cmp-stage-badge stage-${(lead.stage || 'new').toLowerCase().replace(/\s+/g, '-')}`}
                                                style={{ fontSize: '10px', padding: '1px 6px' }}
                                              >
                                                {lead.stage || 'New'}
                                              </span>
                                              {isSent && (
                                                <span className="cmp-lead-sent-tag">
                                                  <i className="fas fa-check-circle" /> Sent
                                                </span>
                                              )}
                                            </div>
                                            <span
                                              title={`${lead.title || 'Prospect'} • ${lead.company || 'Unknown Org'}`}
                                            >
                                              {lead.title || 'Prospect'} •{' '}
                                              {lead.company || 'Unknown Org'}
                                            </span>
                                          </div>
                                        </td>
                                        <td>
                                          <span className="cmp-channel-info-pill">
                                            <i className={channelInfo.icon} />{' '}
                                            {channelInfo.label || channelInfo.channel}
                                            {channelInfo.isFallback && (
                                              <span className="cmp-sub-tag fallback">Fallback</span>
                                            )}
                                          </span>
                                          {channelInfo.contactValue ? (
                                            <span
                                              style={{
                                                display: 'block',
                                                fontSize: '11px',
                                                color: '#64748b',
                                                marginTop: '2px',
                                              }}
                                            >
                                              {channelInfo.label || channelInfo.channel}:{' '}
                                              {channelInfo.contactValue}
                                            </span>
                                          ) : channelInfo.routeType === 'unreachable' ? (
                                            <span
                                              style={{
                                                display: 'block',
                                                fontSize: '11px',
                                                color: '#ef4444',
                                                marginTop: '2px',
                                              }}
                                            >
                                              {channelInfo.label}
                                            </span>
                                          ) : null}
                                        </td>
                                        <td>
                                          {isSent ? (
                                            <span className="cmp-tag-sent">
                                              <i className="fas fa-check-circle" /> Sent
                                            </span>
                                          ) : hasLeadReplied(
                                              lead,
                                              formData?.journey,
                                              leadResponsesMap
                                            ) ? (
                                            <span className="cmp-tag-replied-stop">
                                              <i className="fas fa-hand-paper" /> Stopped
                                            </span>
                                          ) : (
                                            <span className="cmp-tag-notsent">
                                              <i className="fas fa-clock" /> Not Sent
                                            </span>
                                          )}
                                        </td>
                                        <td>
                                          <span
                                            style={{
                                              fontSize: '11px',
                                              color: isSent ? '#15803d' : '#64748b',
                                            }}
                                          >
                                            {isSent
                                              ? delivery.sentAt || 'Sent on Schedule'
                                              : hasLeadReplied(
                                                    lead,
                                                    formData?.journey,
                                                    leadResponsesMap
                                                  )
                                                ? 'Auto-Outreach Stopped'
                                                : 'Pending Dispatch'}
                                          </span>
                                        </td>
                                        {!isReadOnly && (
                                          <td style={{ textAlign: 'right' }}>
                                            <button
                                              type="button"
                                              className={`cmp-btn-toggle-send ${isSent ? 'mark-unsent' : 'mark-sent'}`}
                                              onClick={() =>
                                                handleToggleLeadSend(activeDetailIdx, leadKey)
                                              }
                                              title={isSent ? 'Mark as Not Sent' : 'Mark as Sent'}
                                            >
                                              {isSent ? 'Mark Unsent' : 'Mark Sent'}
                                            </button>
                                          </td>
                                        )}
                                      </tr>
                                    );
                                  })
                                )}
                              </tbody>
                            </table>
                          </div>

                          {/* Section 2: Prospect Responses */}
                          <div
                            className="cmp-response-records-section"
                            style={{ marginTop: '14px' }}
                          >
                            <div className="cmp-response-heading-row">
                              <h5
                                className="cmp-response-heading-title"
                                style={{ fontSize: '12px' }}
                              >
                                <i className="fas fa-comments-dollar text-primary" /> Message
                                Responses
                              </h5>
                            </div>
                            <div className="cmp-response-stats-grid">
                              <div className="cmp-response-card positive">
                                <i className="fas fa-thumbs-up" />
                                <div className="cmp-response-card-content">
                                  <span className="cmp-response-card-label">Positive</span>
                                  <span className="cmp-response-card-val">
                                    {positiveLeads.length}
                                  </span>
                                </div>
                              </div>
                              <div className="cmp-response-card negative">
                                <i className="fas fa-thumbs-down" />
                                <div className="cmp-response-card-content">
                                  <span className="cmp-response-card-label">Negative</span>
                                  <span className="cmp-response-card-val">
                                    {negativeLeads.length}
                                  </span>
                                </div>
                              </div>
                              <div className="cmp-response-card noreply">
                                <i className="fas fa-comment-slash" />
                                <div className="cmp-response-card-content">
                                  <span className="cmp-response-card-label">No Reply</span>
                                  <span className="cmp-response-card-val">
                                    {noReplyLeads.length}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="cmp-drawer-table-wrap" style={{ marginTop: '10px' }}>
                              <table className="cmp-drawer-leads-table">
                                <thead>
                                  <tr>
                                    <th>Lead</th>
                                    <th>Recorded Response</th>
                                    <th>Notes / Reply Snippet</th>
                                    <th>Timestamp</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {stepActiveLeads.length === 0 ? (
                                    <tr>
                                      <td
                                        colSpan={4}
                                        style={{
                                          textAlign: 'center',
                                          padding: '12px',
                                          color: '#64748b',
                                        }}
                                      >
                                        No active leads to record responses for.
                                      </td>
                                    </tr>
                                  ) : (
                                    stepActiveLeads.map((lead) => {
                                      const leadKey = String(
                                        lead.id || lead._id || lead.email || lead.name
                                      );
                                      const resp =
                                        getLeadResponseRecord(lead, stepResponsesMap) || {};
                                      const rawRespType =
                                        resp.responseType ||
                                        lead.leadResponse ||
                                        (lead.stage === 'Qualified' || lead.stage === 'Won'
                                          ? 'interested'
                                          : lead.stage === 'Lost' &&
                                              (lead.lostReason === 'Not interested' ||
                                                lead.lostReason === 'not_interested')
                                            ? 'not_interested'
                                            : 'no_reply');
                                      const currentRespType =
                                        rawRespType === 'positive' || rawRespType === 'interested'
                                          ? 'interested'
                                          : rawRespType === 'negative' ||
                                              rawRespType === 'not_interested'
                                            ? 'not_interested'
                                            : 'no_reply';
                                      const delivery =
                                        getLeadDeliveryRecord(lead, stepDeliveryMap) || {};
                                      return (
                                        <tr key={leadKey}>
                                          <td>
                                            <strong>{lead.name || 'Unnamed'}</strong>
                                            <span
                                              style={{
                                                display: 'block',
                                                fontSize: '11px',
                                                color: '#64748b',
                                              }}
                                            >
                                              {lead.email || lead.phone || 'No direct contact'}
                                            </span>
                                          </td>
                                          <td>
                                            {isReadOnly ? (
                                              <span
                                                className={`cmp-badge-${
                                                  currentRespType === 'interested'
                                                    ? 'positive'
                                                    : currentRespType === 'not_interested'
                                                      ? 'negative'
                                                      : 'noreply'
                                                }`}
                                              >
                                                {currentRespType === 'interested' ? (
                                                  <>
                                                    <i className="fas fa-thumbs-up" /> Positive
                                                    (Interested)
                                                  </>
                                                ) : currentRespType === 'not_interested' ? (
                                                  <>
                                                    <i className="fas fa-thumbs-down" /> Negative
                                                    (Not Interested)
                                                  </>
                                                ) : (
                                                  <>
                                                    <i className="fas fa-comment-slash" /> No Reply
                                                  </>
                                                )}
                                              </span>
                                            ) : (
                                              <select
                                                className={`cmp-field-select-xs cmp-resp-select cmp-resp-${
                                                  currentRespType === 'interested'
                                                    ? 'positive'
                                                    : currentRespType === 'not_interested'
                                                      ? 'negative'
                                                      : 'noreply'
                                                }`}
                                                value={currentRespType}
                                                onChange={(e) =>
                                                  handleUpdateLeadResponse(
                                                    activeDetailIdx,
                                                    leadKey,
                                                    e.target.value,
                                                    extractNoteText(resp.note) ||
                                                      extractNoteText(lead.notes) ||
                                                      extractNoteText(lead.note) ||
                                                      ''
                                                  )
                                                }
                                              >
                                                <option value="interested">
                                                  Positive (Meeting / Interested)
                                                </option>
                                                <option value="not_interested">
                                                  Negative (Not Interested / Unsubscribe)
                                                </option>
                                                <option value="no_reply">
                                                  No Reply (Awaiting Response)
                                                </option>
                                              </select>
                                            )}
                                          </td>
                                          <td>
                                            {isReadOnly ? (
                                              <span
                                                style={{ fontSize: '11.5px', color: '#334155' }}
                                              >
                                                {extractNoteText(resp.note) ||
                                                  extractNoteText(lead.notes) ||
                                                  extractNoteText(lead.note) ||
                                                  '—'}
                                              </span>
                                            ) : (
                                              <input
                                                type="text"
                                                className="cmp-field-input-sm"
                                                style={{
                                                  width: '100%',
                                                  textAlign: 'left',
                                                  background: '#ffffff',
                                                  border: '1px solid #cbd5e1',
                                                }}
                                                placeholder="Add reply snippet / notes..."
                                                value={
                                                  extractNoteText(resp.note) ||
                                                  extractNoteText(lead.notes) ||
                                                  extractNoteText(lead.note) ||
                                                  ''
                                                }
                                                onChange={(e) =>
                                                  handleUpdateLeadResponse(
                                                    activeDetailIdx,
                                                    leadKey,
                                                    currentRespType,
                                                    e.target.value
                                                  )
                                                }
                                              />
                                            )}
                                          </td>
                                          <td>
                                            <span style={{ fontSize: '11px', color: '#64748b' }}>
                                              {resp.respondedAt ||
                                                lead.respondedAt ||
                                                (delivery.sent ? 'Logged on Dispatch' : '—')}
                                            </span>
                                          </td>
                                        </tr>
                                      );
                                    })
                                  )}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}
          </>
        )}
      </div>

      {/* ── FREQUENCY, TIMING & CADENCE GUARDRAILS ── */}
      <div className="cmp-timing-guardrails-card">
        <div className="cmp-timing-header">
          <div className="cmp-timing-title-row">
            <div className="cmp-timing-badge-icon">
              <i className="fas fa-business-time" />
            </div>
            <div className="cmp-timing-title-text">
              <h4 className="cmp-timing-heading">
                Outreach Frequency, Business Hours & Quiet-Hour Controls
              </h4>
              <p className="cmp-timing-subheading">
                Configure campaign sending windows, prospect local time, quiet hours, and frequency
                limits. If an outreach step falls outside the allowed window, the system{' '}
                <strong>does NOT send immediately</strong> and automatically{' '}
                <strong>reschedules outreach to the next eligible sending period</strong>.
              </p>
            </div>
          </div>
          <div className="cmp-timing-presets-wrap">
            <span className="cmp-timing-presets-label">Presets:</span>
            {TIMING_PRESETS.map((p) => {
              const isSelected =
                timingConfig.sendWindowStart === p.config.sendWindowStart &&
                timingConfig.sendWindowEnd === p.config.sendWindowEnd &&
                timingConfig.maxTouchesPerPeriod === p.config.maxTouchesPerPeriod &&
                timingConfig.minDelayHours === p.config.minDelayHours;
              return (
                <button
                  key={p.id}
                  type="button"
                  disabled={isReadOnly}
                  className={`cmp-timing-preset-btn ${isSelected ? 'active' : ''}`}
                  onClick={() => applyTimingPreset(p)}
                  title={p.description}
                >
                  {p.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="cmp-timing-controls-grid">
          {/* Column 1: Sending Window & Business Hours */}
          <div
            ref={sendingWindowColRef}
            className={`cmp-timing-col ${highlightSendingWindow ? 'cmp-timing-col-highlight' : ''}`}
          >
            <h5 className="cmp-timing-col-title">
              <i className="fas fa-calendar-alt" /> Allowed Sending Window
            </h5>
            <div>
              <label className="cmp-mini-label" style={{ marginBottom: '6px', display: 'block' }}>
                Allowed Business Days:
              </label>
              <div className="cmp-timing-days-pills">
                {[
                  { num: 1, label: 'Mon' },
                  { num: 2, label: 'Tue' },
                  { num: 3, label: 'Wed' },
                  { num: 4, label: 'Thu' },
                  { num: 5, label: 'Fri' },
                  { num: 6, label: 'Sat' },
                  { num: 7, label: 'Sun' },
                ].map((d) => {
                  const active =
                    Array.isArray(timingConfig.businessDays) &&
                    timingConfig.businessDays.includes(d.num);
                  return (
                    <button
                      key={d.num}
                      type="button"
                      disabled={isReadOnly}
                      className={`cmp-timing-day-pill ${active ? 'active' : ''}`}
                      onClick={() => toggleBusinessDay(d.num)}
                      title={`Toggle ${d.label}`}
                    >
                      {d.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="cmp-mini-label" style={{ marginBottom: '4px', display: 'block' }}>
                Sending Hours (Local Time):
              </label>
              <div className="cmp-timing-hours-row">
                <input
                  type="time"
                  disabled={isReadOnly}
                  value={timingConfig.sendWindowStart || '09:00'}
                  onChange={(e) => updateTimingConfig('sendWindowStart', e.target.value)}
                  title="Window Start Time"
                />
                <span style={{ fontSize: '11px', color: '#64748b' }}>to</span>
                <input
                  type="time"
                  disabled={isReadOnly}
                  value={timingConfig.sendWindowEnd || '18:00'}
                  onChange={(e) => updateTimingConfig('sendWindowEnd', e.target.value)}
                  title="Window End Time"
                />
              </div>
            </div>

            <div>
              <label className="cmp-mini-label" style={{ marginBottom: '6px', display: 'block' }}>
                Campaign Timezone:
              </label>
              <div
                className="cmp-timing-local-badge"
                title={`Detected User Timezone: ${userLocalTz.timezone}`}
                style={{ marginTop: 0 }}
              >
                <i className="fas fa-globe-asia" />
                <span>
                  <strong>{userLocalTz.timezone}</strong> ({userLocalTz.abbreviation}) · Local Time:{' '}
                  <strong>{userLocalTz.formattedTime}</strong>
                </span>
              </div>
            </div>
          </div>

          {/* Column 2: Quiet Hours & Protections */}
          <div className="cmp-timing-col">
            <h5 className="cmp-timing-col-title">
              <i className="fas fa-moon" /> Quiet Hours & Date Protection
            </h5>

            <label className="cmp-timing-check-row">
              <input
                type="checkbox"
                disabled={isReadOnly}
                checked={Boolean(timingConfig.excludeWeekends)}
                onChange={(e) => updateTimingConfig('excludeWeekends', e.target.checked)}
              />
              <span>
                <strong>Skip Weekends:</strong> Prohibit Saturday & Sunday automated dispatches
              </span>
            </label>

            <label className="cmp-timing-check-row">
              <input
                type="checkbox"
                disabled={isReadOnly}
                checked={Boolean(timingConfig.quietHoursEnabled)}
                onChange={(e) => updateTimingConfig('quietHoursEnabled', e.target.checked)}
              />
              <span>
                <strong>Strict Quiet Hours:</strong> Prohibit outreach from{' '}
                <code>{timingConfig.quietHoursStart || '18:00'}</code> to{' '}
                <code>{timingConfig.quietHoursEnd || '09:00'}</code>
              </span>
            </label>
          </div>

          {/* Column 3: Frequency Caps & Pacing */}
          <div className="cmp-timing-col">
            <h5 className="cmp-timing-col-title">
              <i className="fas fa-tachometer-alt" /> Frequency Caps & Pacing
            </h5>

            <div className="cmp-timing-cap-item">
              <div>
                <strong>Max Automated Outreach:</strong>
                <p style={{ margin: 0, fontSize: '11px', color: '#64748b' }}>
                  Touches per rolling window
                </p>
              </div>
              <div className="cmp-timing-cap-inputs">
                <input
                  type="number"
                  min="1"
                  max="10"
                  disabled={isReadOnly}
                  value={timingConfig.maxTouchesPerPeriod || 3}
                  onChange={(e) =>
                    updateTimingConfig('maxTouchesPerPeriod', parseInt(e.target.value, 10) || 3)
                  }
                  title="Max touches"
                />
                <span style={{ fontSize: '11px', color: '#64748b' }}>per</span>
                <input
                  type="number"
                  min="1"
                  max="30"
                  disabled={isReadOnly}
                  value={timingConfig.periodDays || 7}
                  onChange={(e) =>
                    updateTimingConfig('periodDays', parseInt(e.target.value, 10) || 7)
                  }
                  title="Window days"
                />
                <span style={{ fontSize: '11px', color: '#64748b' }}>days</span>
              </div>
            </div>

            <div className="cmp-timing-cap-item">
              <div>
                <strong>Minimum Delay:</strong>
                <p style={{ margin: 0, fontSize: '11px', color: '#64748b' }}>
                  Spacing between consecutive touches
                </p>
              </div>
              <div className="cmp-timing-cap-inputs">
                <input
                  type="number"
                  min="1"
                  max="168"
                  disabled={isReadOnly}
                  value={timingConfig.minDelayHours || 48}
                  onChange={(e) =>
                    updateTimingConfig('minDelayHours', parseInt(e.target.value, 10) || 48)
                  }
                />
                <span style={{ fontSize: '11px', color: '#64748b' }}>hours</span>
              </div>
            </div>

            <div className="cmp-timing-cap-item">
              <div>
                <strong>Max Campaign Touches:</strong>
                <p style={{ margin: 0, fontSize: '11px', color: '#64748b' }}>
                  Total touches limit per recipient
                </p>
              </div>
              <div className="cmp-timing-cap-inputs">
                <input
                  type="number"
                  min="1"
                  max="20"
                  disabled={isReadOnly}
                  value={timingConfig.maxCampaignTouches || 5}
                  onChange={(e) =>
                    updateTimingConfig('maxCampaignTouches', parseInt(e.target.value, 10) || 5)
                  }
                />
                <span style={{ fontSize: '11px', color: '#64748b' }}>touches</span>
              </div>
            </div>
            {Array.isArray(formData.journey) &&
              formData.journey.length > (timingConfig.maxCampaignTouches || 5) && (
                <div
                  className="cmp-timing-cap-warning"
                  style={{
                    marginTop: '6px',
                    fontSize: '11px',
                    color: '#dc2626',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px',
                    padding: '4px 8px',
                    background: '#fef2f2',
                    borderRadius: '4px',
                    border: '1px solid #fecaca',
                  }}
                >
                  <i className="fas fa-exclamation-circle" />
                  <span>
                    <strong>
                      {formData.journey.length - (timingConfig.maxCampaignTouches || 5)} step(s)
                    </strong>{' '}
                    exceed this limit and will be greyed/blocked.
                  </span>
                </div>
              )}
          </div>
        </div>

        {/* ── LIVE CADENCE RESCHEDULING SIMULATOR ── */}
        {formData.journey && formData.journey.length > 0 && (
          <div className="cmp-cadence-simulator-card">
            <div className="cmp-simulator-header">
              <div>
                <h5 className="cmp-simulator-heading">
                  <i className="fas fa-magic text-primary" /> Live Cadence Rescheduling Simulator &
                  Inspector
                </h5>
                <p className="cmp-simulator-subheading">
                  Automated rescheduling projection: if a touchpoint lands on a weekend or after
                  hours, outreach is held and automatically rescheduled to{' '}
                  <strong>{timingConfig.sendWindowStart || '09:00'}</strong> of the next eligible
                  business day.
                </p>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '11.5px', fontWeight: 600, color: '#475569' }}>
                  Timezone:
                </span>
                <span style={{ fontSize: '12px', fontWeight: 600, color: '#0f172a' }}>
                  {userLocalTz.timezone} ({userLocalTz.abbreviation})
                </span>
              </div>
            </div>

            <div className="cmp-simulator-table-wrap">
              <table className="cmp-simulator-table">
                <thead>
                  <tr>
                    <th style={{ width: '85px' }}>Touchpoint</th>
                    <th>Channel & Overview</th>
                    <th>Nominal Plan</th>
                    <th>Actual Scheduled Dispatch</th>
                    <th>Guardrail Evaluation</th>
                  </tr>
                </thead>
                <tbody>
                  {simulatedCadence.map((step) => {
                    const isBlocked = Boolean(step.isBlocked);
                    return (
                      <tr
                        key={step.stepNumber}
                        className={isBlocked ? 'cmp-simulator-row-blocked' : ''}
                      >
                        <td>
                          <strong style={{ color: isBlocked ? '#64748b' : '#2563eb' }}>
                            Step {step.stepNumber}
                          </strong>
                        </td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <i
                              className={getChannelIcon(step.channel)}
                              style={{ color: isBlocked ? '#94a3b8' : '#64748b' }}
                            />
                            <span style={{ color: isBlocked ? '#64748b' : undefined }}>
                              <strong>{step.channel}:</strong> {step.stepName}
                            </span>
                          </div>
                        </td>
                        <td>
                          <div style={{ color: isBlocked ? '#94a3b8' : undefined }}>
                            Day {step.nominalDay} •{' '}
                            <span style={{ color: isBlocked ? '#94a3b8' : '#64748b' }}>
                              {step.nominalFormatted}
                            </span>
                          </div>
                        </td>
                        <td>
                          {isBlocked ? (
                            <span
                              style={{
                                color: '#dc2626',
                                fontWeight: 600,
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '5px',
                              }}
                            >
                              <i className="fas fa-ban" /> Blocked (Will Not Send)
                            </span>
                          ) : (
                            <strong>{step.actualFormatted}</strong>
                          )}
                        </td>
                        <td>
                          {isBlocked ? (
                            <span className="cmp-timing-badge blocked" title={step.reason}>
                              <i className="fas fa-ban" /> Blocked: Max touches reached
                            </span>
                          ) : step.isBeyondCampaignEnd ? (
                            <span
                              className="cmp-timing-badge blocked"
                              style={{
                                background: '#fef2f2',
                                color: '#b91c1c',
                                border: '1px solid #fecaca',
                              }}
                              title={step.reason}
                            >
                              <i className="fas fa-calendar-times" /> Exceeds Campaign End (
                              {campaignEndDate})
                            </span>
                          ) : step.isRescheduled ? (
                            <span
                              className="cmp-timing-badge rescheduled"
                              title={step.reasons.join(', ')}
                            >
                              <i className="fas fa-clock" /> Rescheduled: {step.reason}
                            </span>
                          ) : (
                            <span className="cmp-timing-badge on-schedule">
                              <i className="fas fa-check-circle" /> On-Schedule Window
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* ── AUTOMATED STOP RULES ── */}
      <div className="cmp-stop-rules-card">
        <h4>
          <i className="fas fa-ban text-danger" /> Automated Stop Rules & Sending Guardrails
        </h4>
        <div className="cmp-rules-grid">
          <div className="cmp-rule-item">
            <i className="fas fa-check-circle text-success" />
            <div>
              <strong>Positive Reply Received:</strong>
              <p>
                Halt automation immediately, create Sales Task in CRM, and notify account owner.
              </p>
            </div>
          </div>
          <div className="cmp-rule-item">
            <i className="fas fa-calendar-check text-primary" />
            <div>
              <strong>Meeting Booked:</strong>
              <p>Halt lead gen sequence and advance prospect to Opportunity Pipeline.</p>
            </div>
          </div>
          <div className="cmp-rule-item">
            <i className="fas fa-user-slash text-danger" />
            <div>
              <strong>Unsubscribe / Opt-out:</strong>
              <p>Instantly suppress contact point across all workspace campaigns.</p>
            </div>
          </div>
          <div className="cmp-rule-item">
            <i className="fas fa-shield-alt text-primary" />
            <div>
              <strong>Automated Rescheduling Guarantee:</strong>
              <p>
                Outreach is strictly bounded by {timingConfig.sendWindowStart || '09:00'}–
                {timingConfig.sendWindowEnd || '18:00'} prospect local time (max{' '}
                {timingConfig.maxTouchesPerPeriod || 3} touches / {timingConfig.periodDays || 7}{' '}
                days). Never sends during quiet hours.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ── GUARDRAIL VIOLATION DOUBLE-CONFIRMATION MODAL ── */}
      {guardrailModal.isOpen && (
        <div className="cmp-guardrail-modal-overlay" role="dialog" aria-modal="true">
          <div className="cmp-guardrail-modal">
            <div className="cmp-guardrail-modal-header">
              <i className="fas fa-exclamation-triangle" />
              <h4>Cadence Guardrail Warning — Confirmation Required</h4>
            </div>
            <div className="cmp-guardrail-modal-body">
              <div className="cmp-guardrail-warning-box">
                <strong>Schedule Modification Alert:</strong>
                <ul style={{ margin: '8px 0 0 18px', padding: 0 }}>
                  {guardrailModal.violations.map((v, i) => (
                    <li key={i} style={{ marginBottom: '4px' }}>
                      {v}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="cmp-guardrail-comparison">
                <div className="cmp-comparison-box proposed">
                  <strong>Your Proposed Schedule:</strong>
                  <div style={{ marginTop: '4px', fontSize: '12px' }}>
                    Day {guardrailModal.proposedDay} • {guardrailModal.proposedDate} at{' '}
                    {guardrailModal.proposedTime}
                  </div>
                  <div style={{ marginTop: '2px', fontSize: '10.5px', color: '#b91c1c' }}>
                    (Breaches cadence/pacing rules)
                  </div>
                </div>

                <div className="cmp-comparison-box recommended">
                  <strong>Recommended Compliant:</strong>
                  <div style={{ marginTop: '4px', fontSize: '12px' }}>
                    Day {guardrailModal.recommendedDay} • {guardrailModal.recommendedDate} at{' '}
                    {guardrailModal.recommendedTime}
                  </div>
                  <div style={{ marginTop: '2px', fontSize: '10.5px', color: '#047857' }}>
                    (Complies with min delay &amp; business days)
                  </div>
                </div>
              </div>

              <p style={{ margin: 0, fontSize: '11.5px', color: '#64748b' }}>
                {guardrailModal.isDurationViolation
                  ? `Campaign duration boundary is enforced (${campaignStartDate} to ${campaignEndDate}, ${campaignDurationDays} days). Steps cannot be scheduled outside this window.`
                  : 'Would you like to auto-adjust to the recommended compliant date/time (with pacing ripple to subsequent steps), or double-confirm to override the rule?'}
              </p>
            </div>
            <div className="cmp-guardrail-modal-actions">
              <button
                type="button"
                className="cmp-btn-guardrail-cancel"
                onClick={() => setGuardrailModal((prev) => ({ ...prev, isOpen: false }))}
              >
                Cancel
              </button>
              <button
                type="button"
                className="cmp-btn-guardrail-override"
                disabled={guardrailModal.isDurationViolation}
                title={
                  guardrailModal.isDurationViolation
                    ? `Cannot override campaign duration boundary (ends ${campaignEndDate}). Steps must occur within Step 1 duration.`
                    : 'Double-confirm and override timing guardrail'
                }
                style={{
                  opacity: guardrailModal.isDurationViolation ? 0.45 : 1,
                  cursor: guardrailModal.isDurationViolation ? 'not-allowed' : 'pointer',
                }}
                onClick={() => {
                  if (guardrailModal.isDurationViolation) return;
                  const { stepIdx, proposedDate, proposedTime } = guardrailModal;
                  setGuardrailModal((prev) => ({ ...prev, isOpen: false }));
                  applyStepDateAndRipple(
                    stepIdx,
                    proposedDate,
                    proposedTime,
                    formData.journey || []
                  );
                }}
              >
                {guardrailModal.isDurationViolation
                  ? 'Duration Bound Enforced'
                  : 'Double-Confirm & Override'}
              </button>
              <button
                type="button"
                className="cmp-btn-guardrail-adjust"
                onClick={() => {
                  const { stepIdx, recommendedDate, recommendedTime } = guardrailModal;
                  setGuardrailModal((prev) => ({ ...prev, isOpen: false }));
                  applyStepDateAndRipple(
                    stepIdx,
                    recommendedDate,
                    recommendedTime,
                    formData.journey || []
                  );
                }}
              >
                Auto-Adjust to Recommended
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── ALLOWED SENDING WINDOW WARNING MODAL ── */}
      {sendingWindowWarningModal.isOpen && (
        <div
          className="cmp-guardrail-modal-overlay cmp-sending-window-modal-overlay"
          role="dialog"
          aria-modal="true"
        >
          <div className="cmp-guardrail-modal">
            <div
              className="cmp-guardrail-modal-header"
              style={{ background: '#fffbeb', borderBottom: '1px solid #fde68a' }}
            >
              <i className="fas fa-exclamation-triangle" style={{ color: '#d97706' }} />
              <h4>Allowed Sending Window Warning</h4>
            </div>
            <div className="cmp-guardrail-modal-body">
              <div className="cmp-guardrail-warning-box">
                <strong style={{ color: '#b45309' }}>Schedule Alert:</strong>
                <p style={{ margin: '6px 0 0 0', fontWeight: 600, color: '#92400e' }}>
                  {sendingWindowWarningModal.message || 'Must follow the allow sending window'}
                </p>
              </div>

              <div className="cmp-guardrail-comparison">
                <div className="cmp-comparison-box proposed">
                  <strong>Your Input Time:</strong>
                  <div style={{ marginTop: '4px', fontSize: '13px', fontWeight: 700 }}>
                    {sendingWindowWarningModal.proposedTime}
                  </div>
                  <div style={{ marginTop: '2px', fontSize: '10.5px', color: '#b91c1c' }}>
                    {sendingWindowWarningModal.isBeforeStart
                      ? `Earlier than start time (${sendingWindowWarningModal.currentWindowStart})`
                      : `Later than end time (${sendingWindowWarningModal.currentWindowEnd})`}
                  </div>
                </div>

                <div className="cmp-comparison-box recommended">
                  <strong>Allowed Sending Hours:</strong>
                  <div style={{ marginTop: '4px', fontSize: '13px', fontWeight: 700 }}>
                    {sendingWindowWarningModal.currentWindowStart} –{' '}
                    {sendingWindowWarningModal.currentWindowEnd}
                  </div>
                  <div style={{ marginTop: '2px', fontSize: '10.5px', color: '#047857' }}>
                    Configured Business Hours
                  </div>
                </div>
              </div>

              <p style={{ margin: 0, fontSize: '12px', color: '#64748b', lineHeight: 1.5 }}>
                Outreach dispatches must follow the allowed sending window. If you click{' '}
                <strong>Ignore</strong>, the sending hours will automatically expand to{' '}
                <strong>
                  {sendingWindowWarningModal.newProposedStart} –{' '}
                  {sendingWindowWarningModal.newProposedEnd}
                </strong>
                , and the page will scroll to the Allowed Sending Window settings.
              </p>
            </div>
            <div className="cmp-guardrail-modal-actions">
              <button
                type="button"
                className="cmp-btn-guardrail-cancel"
                onClick={() => setSendingWindowWarningModal((prev) => ({ ...prev, isOpen: false }))}
              >
                Cancel
              </button>
              <button
                type="button"
                className="cmp-btn-guardrail-adjust"
                onClick={handleIgnoreSendingWindowWarning}
              >
                Ignore
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── "LEAD INTERESTED" CONVERSION & CRM MODAL ── */}
      {leadInterestedModal.isOpen && (
        <div
          className="cmp-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="lead-interested-modal-title"
        >
          <div className="cmp-lead-interested-modal">
            <div className="cmp-interested-modal-header">
              <div className="cmp-interested-modal-title-wrap">
                <div className="cmp-interested-badge-icon">
                  <i className="fas fa-handshake" />
                </div>
                <div>
                  <h4 id="lead-interested-modal-title" className="cmp-interested-heading">
                    Lead interested
                  </h4>
                  <p className="cmp-interested-subheading">
                    Lead converted to interested! Assign to a campaign representative and configure
                    follow-up tasks to sync with Lead Pipeline CRM.
                  </p>
                </div>
              </div>
              <button
                type="button"
                className="cmp-modal-close-btn"
                onClick={handleCloseLeadInterestedModal}
                aria-label="Close modal"
              >
                &times;
              </button>
            </div>

            <div className="cmp-interested-modal-body">
              {/* Lead Card Overview */}
              <div className="cmp-interested-lead-card">
                <div className="cmp-interested-lead-avatar">
                  <i className="fas fa-user-check" />
                </div>
                <div className="cmp-interested-lead-details">
                  <div className="cmp-interested-lead-name-row">
                    <strong>{leadInterestedModal.lead?.name || 'Prospect'}</strong>
                    <span className="cmp-tag-qualified">
                      <i className="fas fa-check-circle" /> Qualified Lead
                    </span>
                  </div>
                  <span className="cmp-interested-lead-meta">
                    {leadInterestedModal.lead?.title ||
                      leadInterestedModal.lead?.jobTitle ||
                      'Decision Maker'}{' '}
                    • {leadInterestedModal.lead?.company || 'Organization'}
                  </span>
                  {(leadInterestedModal.lead?.email || leadInterestedModal.lead?.phone) && (
                    <div className="cmp-interested-lead-contact">
                      {leadInterestedModal.lead?.email && (
                        <span>
                          <i className="fas fa-envelope" /> {leadInterestedModal.lead.email}
                        </span>
                      )}
                      {leadInterestedModal.lead?.phone && (
                        <span>
                          <i className="fas fa-phone" /> {leadInterestedModal.lead.phone}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Campaign Member Assignment Select */}
              <div className="cmp-interested-section">
                <label className="cmp-interested-section-label">
                  <i className="fas fa-user-tag text-primary" /> Assign to Campaign Member:
                </label>
                <p className="cmp-interested-section-desc">
                  Select a team member who is part of this campaign to take ownership of this
                  interested lead.
                </p>
                <select
                  className="cmp-interested-member-select"
                  value={leadInterestedModal.assignedMember}
                  onChange={(e) =>
                    setLeadInterestedModal((prev) => ({
                      ...prev,
                      assignedMember: e.target.value,
                    }))
                  }
                >
                  <option value="">-- Choose Campaign Team Member --</option>
                  {campaignMembers.map((m) => {
                    const mId = m.userId || m.id || String(m);
                    const mName = m.name || m.email || String(m);
                    const mRole = m.role || 'Campaign Rep';
                    return (
                      <option key={mId} value={mId}>
                        {mName} ({mRole})
                      </option>
                    );
                  })}
                </select>
                {campaignMembers.length === 0 && (
                  <div className="cmp-interested-warning-box">
                    <i className="fas fa-exclamation-triangle" />
                    <span>
                      No members currently assigned to this campaign. You can add team members in
                      the Campaign Team Members section above.
                    </span>
                  </div>
                )}
              </div>

              {/* Response Note / Feedback */}
              <div className="cmp-interested-section">
                <label className="cmp-interested-section-label">
                  <i className="fas fa-comment-dots text-primary" /> Response Notes / Meeting
                  Request:
                </label>
                <input
                  type="text"
                  className="cmp-field-input-sm"
                  style={{ width: '100%', background: '#fff', border: '1px solid #cbd5e1' }}
                  placeholder="e.g. Requested product consultation next Tuesday..."
                  value={leadInterestedModal.note || ''}
                  onChange={(e) =>
                    setLeadInterestedModal((prev) => ({ ...prev, note: e.target.value }))
                  }
                />
              </div>

              {/* Follow up task section from lead pipeline crm-detail-modal */}
              <div className="crm-detail-section" style={{ marginTop: '16px' }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '8px',
                  }}
                >
                  <h4 style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: '#1e293b' }}>
                    Follow up task
                  </h4>
                  <span style={{ fontSize: '11.5px', color: '#64748b' }}>
                    {leadInterestedModal.tasks.length} task
                    {leadInterestedModal.tasks.length !== 1 ? 's' : ''} queued
                  </span>
                </div>
                <div className="crm-tasks">
                  <div className="crm-task-input-row">
                    <textarea
                      className="crm-task-text"
                      placeholder="First line = task title&#10;Following lines = description"
                      rows={3}
                      value={leadInterestedModal.taskInput}
                      onChange={(e) =>
                        setLeadInterestedModal((prev) => ({
                          ...prev,
                          taskInput: e.target.value,
                        }))
                      }
                    />
                    <input
                      type="date"
                      className="crm-task-date"
                      min={formatLocalDate(new Date())}
                      value={leadInterestedModal.taskDueDate}
                      onChange={(e) =>
                        setLeadInterestedModal((prev) => ({
                          ...prev,
                          taskDueDate: e.target.value,
                        }))
                      }
                    />
                  </div>
                  <div className="crm-task-input-row">
                    <select
                      className="crm-task-select"
                      value={leadInterestedModal.taskPriority}
                      onChange={(e) =>
                        setLeadInterestedModal((prev) => ({
                          ...prev,
                          taskPriority: e.target.value,
                        }))
                      }
                    >
                      <option>Low</option>
                      <option>Medium</option>
                      <option>High</option>
                    </select>
                    <button
                      type="button"
                      className="crm-task-add"
                      onClick={handleAddTaskToInterestedModal}
                      disabled={!leadInterestedModal.taskInput?.trim()}
                    >
                      + Add
                    </button>
                  </div>

                  {leadInterestedModal.tasks.length > 0 && (
                    <div className="crm-task-list">
                      {leadInterestedModal.tasks.map((t) => {
                        const isDone = t.status === 'completed';
                        return (
                          <div key={t.id} className={`crm-task${isDone ? ' crm-task-done' : ''}`}>
                            <button
                              type="button"
                              className="crm-task-circle"
                              title={
                                isDone
                                  ? 'Click to revert to In progress'
                                  : 'Click to mark as Completed'
                              }
                              onClick={() => handleToggleTaskStatusInInterestedModal(t.id)}
                              aria-label={
                                isDone ? 'Revert task to in progress' : 'Mark task as completed'
                              }
                            />
                            <span className="crm-task-label">
                              <span className="crm-task-text-label">{t.title || t.text}</span>
                              {t.description && (
                                <span className="crm-task-desc">{t.description}</span>
                              )}
                              <span className="crm-task-meta">
                                <span
                                  className={`crm-task-status crm-task-status-${t.status || 'pending'}`}
                                >
                                  {t.status === 'in_progress'
                                    ? 'In progress'
                                    : t.status === 'completed'
                                      ? 'Completed'
                                      : 'Pending'}
                                </span>
                                {t.priority && (
                                  <span
                                    className={`crm-task-priority crm-task-priority-${String(t.priority).toLowerCase()}`}
                                  >
                                    {t.priority}
                                  </span>
                                )}
                                {t.dueDate && <span>Due {t.dueDate}</span>}
                              </span>
                            </span>
                            <div className="crm-task-actions">
                              <select
                                className="crm-task-status-select"
                                value={t.status || 'pending'}
                                onChange={(e) =>
                                  handleUpdateTaskStatusInInterestedModal(t.id, e.target.value)
                                }
                              >
                                <option value="pending">Pending</option>
                                <option value="in_progress">In progress</option>
                                <option value="completed">Completed</option>
                                <option value="cancelled">Cancelled</option>
                              </select>
                              <button
                                type="button"
                                className="crm-task-delete"
                                onClick={() => handleDeleteTaskInInterestedModal(t.id)}
                                title="Delete task"
                              >
                                <i className="fas fa-trash" />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="cmp-interested-modal-footer">
              <button
                type="button"
                className="cmp-btn-secondary"
                onClick={handleCloseLeadInterestedModal}
              >
                Cancel
              </button>
              <button
                type="button"
                className="cmp-btn-primary"
                onClick={handleConfirmSaveLeadInterested}
              >
                <i className="fas fa-check" /> Confirm Interested &amp; Qualify Lead
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
